/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// FLAGGED-FOR-REVIEW-2026-02: This file has residual unused vars / state
// setters / effect-deps that look like DROPPED-FEATURE references, not
// blind-orphan code (state setters never called, computed values never
// rendered, hooks with intentional dep omissions). Reviewer next step:
// walk each warning and decide either RESTORE the callsite that references
// the var, OR delete the var. Do NOT bulk-delete without review — several
// of these signal features that were half-migrated.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ChevronRight, Download, Search, Flag, Users, Share2, FileText, MessageSquare, Send, Trash2, Edit2, Plus, Camera, FileSpreadsheet, Wand2, X, CheckCircle2, Globe, Languages, MapPin, Clock, Moon, Sun, Sunset, Sunrise, Layers, Sparkles, Film, LayoutGrid, CalendarDays, GripVertical, Package, RefreshCw, Loader2, Pencil, Palette } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { SceneEditDialog, BreakdownItemEditor, CATEGORIES } from '../components/EditDialogs';
import { ImageHoverIndicator } from '../components/ImageUpload';
import { toast } from 'sonner';
import StudioView from '../components/breakdown/StudioView';
import AIImportModal from '../components/AIImportModal';
import { mlSafeStyle } from '../utils/mlSafe';
import { getBreakdownItemLabel } from '../utils/breakdownLabel';
import SceneViewModal from '../components/SceneViewModal';
import { ScreenplayText } from '../components/SceneViewModal';
import NavigatorPage from './NavigatorPage';
import EditableField from '../components/EditableField';
import { EntityPanel } from '../components/EntityPanel';
import { ScriptHealthBanner } from '../components/ScriptHealthBanner';
import BreakdownItemEditModal from './art/BreakdownItemEditModal';
import AiSuggestPanel from './art/AiSuggestPanel';

import { formatSceneNumber, countSceneNumbers } from '../utils/sceneNumber';

// ── Module-level Navigator UI helpers (kept here to avoid React's
//    "Do not define components during render" warning) ───────────────────
const NavStat = ({ label, value, color, Icon, testid, tooltip }) => (
  <div
    className="rounded-lg p-5 min-w-[130px] transition-all duration-200"
    style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }}
    onMouseEnter={e => e.currentTarget.style.background = 'var(--cb-surface-h)'}
    onMouseLeave={e => e.currentTarget.style.background = 'var(--cb-surface)'}
    data-testid={testid}
    title={tooltip}
  >
    <div className="flex items-center gap-1.5 mb-2">
      <Icon className="h-3 w-3" style={{ color }} strokeWidth={1.5} />
      <span className="text-[10px] font-semibold uppercase tracking-[0.08em]" style={{ color: 'var(--cb-text-dim)' }}>{label}</span>
    </div>
    <div className="text-3xl font-semibold tracking-tight" style={{ color: 'var(--cb-text)' }}>{value}</div>
  </div>
);

const NavAction = ({ label, color, Icon, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="rounded-lg px-3 py-2 flex items-center gap-2 transition-all duration-200 hover:scale-[1.01]"
    style={{ background: `${color}0F`, border: `1px solid ${color}30`, color }}
  >
    <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
    <span className="text-xs font-semibold">{label}</span>
  </button>
);


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Design tokens — CSS var references, updated by ThemeContext ───────────
const DT = {
  surface:    'var(--cb-bg)',
  card:       'var(--cb-surface)',
  cardHover:  'var(--cb-surface-h)',
  border:     'var(--cb-border)',
  accent:     'var(--cb-accent)',
  accentDim:  'var(--cb-accent-dim)',
  textPri:    'var(--cb-text)',
  textSec:    'var(--cb-text-sec)',
  textDim:    'var(--cb-text-dim)',
};

// ── Scene type visual system ───────────────────────────────────────────────────
const SCENE_TYPE_MAP = {
  'EXT DAY':        { color: '#F5C518', icon: Sun,     label: 'EXT · DAY' },
  'INT DAY':        { color: '#60A5FA', icon: Film,    label: 'INT · DAY' },
  'EXT NIGHT':      { color: '#8B5CF6', icon: Moon,    label: 'EXT · NIGHT' },
  'INT NIGHT':      { color: '#34D399', icon: Layers,  label: 'INT · NIGHT' },
  'EXT DAWN':       { color: '#FB923C', icon: Sunrise, label: 'EXT · DAWN' },
  'EXT DUSK':       { color: '#F97316', icon: Sunset,  label: 'EXT · DUSK' },
  'INT CONTINUOUS': { color: '#94A3B8', icon: Film,    label: 'INT · CONT' },
  'EXT CONTINUOUS': { color: '#64748B', icon: Film,    label: 'EXT · CONT' },
};
const getSceneTypeStyle = (scene) => {
  if (scene.is_flagged) return { color: '#F87171', icon: Flag, label: 'FLAGGED' };
  const k = `${(scene.location_type || 'INT').toUpperCase()} ${(scene.time_of_day || 'DAY').toUpperCase()}`;
  return SCENE_TYPE_MAP[k] || SCENE_TYPE_MAP['INT DAY'];
};

// ── Category chip config (minimal monochrome) ──────────────────────────────────
// Phase 5 (2026-02) — label vocabulary unified with Art Dept post-Phase-2:
//   PROP → "Action Props" (was "Props"; PROP semantic is character-handled)
//   SCENE_PROP → "Scene Props" (NEW chip; Phase 2 parser now emits this)
//   ART_SET_DRESSING → "Set Dressing" (was "Art"; Decision 2(a) display-only)
//   PICTURE_VEHICLE → kept as "P.Vehicle" — scene cards are dense with many
//     chip pills; the compact form reads better in that context. Art Dept's
//     "Picture Vehicle" is used in the wider PropCol column header only.
// Phase 13 Ask B (2026-07) — columns the /art/suggest-props endpoint supports.
const MB_AI_COLUMN_FOR_CATEGORY = {
  PROP:             'action_prop',
  SCENE_PROP:       'scene_prop',
  ART_SET_DRESSING: 'set_dressing',
};

const CAT_CHIPS = {
  PROP:              { label: 'Action Props', color: 'rgba(255,255,255,0.5)' },
  SCENE_PROP:        { label: 'Scene Props',  color: 'rgba(255,255,255,0.5)' },
  COSTUME:           { label: 'Costume',      color: 'rgba(255,255,255,0.5)' },
  ART_SET_DRESSING:  { label: 'Set Dressing', color: 'rgba(255,255,255,0.5)' },
  VFX:               { label: 'VFX',      color: '#60A5FA' },
  STUNT:             { label: 'Stunt',    color: '#F87171' },
  SFX_SPECIAL_EFFECTS: { label: 'SFX',   color: '#FB923C' },
  MAKEUP_HAIR:       { label: 'Makeup',   color: 'rgba(255,255,255,0.5)' },
  SOUND_MUSIC:       { label: 'Sound',    color: 'rgba(255,255,255,0.5)' },
  VEHICLE:           { label: 'Vehicle',  color: 'rgba(255,255,255,0.5)' },
  PICTURE_VEHICLE:   { label: 'P.Vehicle',color: 'rgba(255,255,255,0.5)' },
  ANIMAL:            { label: 'Animal',   color: '#34D399' },
  EXTRA_BACKGROUND:  { label: 'Background Actors',   color: 'rgba(255,255,255,0.5)' },
  PRODUCTION_NOTE:   { label: 'Note',     color: 'rgba(255,255,255,0.3)' },
};

// Component for comment input with @mention autocomplete
const CommentInput = ({ value, onChange, users, onSubmit, disabled }) => {
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const inputRef = useRef(null);

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val);
    
    // Check for @ symbol
    const lastAt = val.lastIndexOf('@');
    if (lastAt !== -1) {
      const afterAt = val.substring(lastAt + 1);
      if (!afterAt.includes(' ') || afterAt.split(' ').length <= 2) {
        setMentionQuery(afterAt.toLowerCase());
        setShowMentions(true);
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };

  const selectMention = (user) => {
    const lastAt = value.lastIndexOf('@');
    const newValue = value.substring(0, lastAt) + '@' + user.full_name + ' ';
    onChange(newValue);
    setShowMentions(false);
    inputRef.current?.focus();
  };

  const filteredUsers = users.filter(u => 
    u.full_name.toLowerCase().includes(mentionQuery) || u.email.toLowerCase().startsWith(mentionQuery)
  ).slice(0, 5);

  return (
    <div className="relative">
      <div className="flex gap-2">
        <Input
          ref={inputRef}
          value={value}
          onChange={handleChange}
          placeholder="Add a note or @mention someone..."
          className="bg-[#0d0d0d] border-[var(--cb-border)] text-[var(--cb-text)]"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !showMentions) {
              e.preventDefault();
              onSubmit();
            }
            if (e.key === 'Escape') setShowMentions(false);
          }}
          data-testid="comment-input"
        />
        <Button
          onClick={onSubmit}
          disabled={disabled || !value.trim()}
          className="bg-[#F5A623] hover:bg-[#d6b539] text-black"
          data-testid="post-comment-btn"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
      {showMentions && filteredUsers.length > 0 && (
        <div className="absolute bottom-full mb-1 left-0 right-12 bg-[var(--cb-surface)] border border-[var(--cb-border)] rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto" data-testid="mention-dropdown">
          {filteredUsers.map(u => (
            <button
              key={u.id}
              type="button"
              onClick={() => selectMention(u)}
              className="w-full text-left px-3 py-2 hover:bg-[var(--cb-surface-h)] text-sm flex items-center gap-2"
              data-testid={`mention-${u.email}`}
            >
              <span className="text-[var(--cb-text)]">{u.full_name}</span>
              <span className="text-[var(--cb-text-sec)] text-xs">{u.department}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Helper to render comment with @mentions highlighted
const renderCommentWithMentions = (content, users) => {
  const parts = content.split(/(@\w+(?:\s\w+)?)/g);
  return parts.map((part, idx) => {
    if (part.startsWith('@')) {
      const name = part.substring(1).toLowerCase();
      const matchedUser = users.find(u => u.full_name.toLowerCase().startsWith(name));
      if (matchedUser) {
        return <span key={`mention-${idx}-${part}`} className="text-[#F5A623] font-medium">{part}</span>;
      }
    }
    return part;
  });
};

// Safely render Fifi AI message lines: convert *bold* markers to <strong> elements
// without using dangerouslySetInnerHTML (avoids XSS from AI-generated content).
const renderMessageLine = (line, li) => {
  if (!line) return <p key={li} className={li > 0 ? 'mt-1' : ''}>&nbsp;</p>;
  const parts = line.split(/(\*[^*]+\*)/g);
  return (
    <p key={li} className={li > 0 ? 'mt-1' : ''}>
      {parts.map((part, pi) =>
        part.startsWith('*') && part.endsWith('*') && part.length > 2
          ? <strong key={pi}>{part.slice(1, -1)}</strong>
          : part
      )}
    </p>
  );
};
// ── Synopsis display helper ──────────────────────────────────────────────────
// Returns English synopsis when toggled (if stored), else original.
// getSynopsis is defined at component level inside MasterBreakdownPage.

// ── FIPR Beat pill — subtle indicator on each scene card ────────────────────
const FIPR_STAGE_CONFIG = {
  FUNNEL:        { letter: 'F', color: '#60a5fa' },
  INCITING:      { letter: 'I', color: '#F5A623' },
  COMPLICATIONS: { letter: 'P', color: '#fb7185' },
  RESOLUTION:    { letter: 'R', color: '#4ade80' },
};
const FIPRBadge = ({ stage }) => {
  const c = FIPR_STAGE_CONFIG[stage];
  if (!c) return null;
  return (
    <span className="text-[9px] font-black px-1.5 py-px rounded shrink-0"
          style={{ background: `${c.color}14`, color: c.color, border: `1px solid ${c.color}28` }}
          title={`FIPR: ${stage.charAt(0) + stage.slice(1).toLowerCase()}`}>
      {c.letter}
    </span>
  );
};

// Computes how well the synopsis language matches the chosen breakdown_language.
// Uses Unicode block detection — no API call needed.
const getMalayalamRatio = (text) => {
  if (!text) return 0;
  const chars = [...text];
  const mlCount = chars.filter(c => { const cp = c.codePointAt(0); return cp >= 0x0D00 && cp <= 0x0D7F; }).length;
  const meaningful = chars.filter(c => c.trim()).length;
  return meaningful > 0 ? mlCount / meaningful : 0;
};

const getLQS = (synopsis, breakdownLanguage) => {
  if (!synopsis || synopsis.trim().length < 8 || breakdownLanguage === 'english') return null;
  const ratio = getMalayalamRatio(synopsis);
  if (breakdownLanguage === 'malayalam') {
    if (ratio >= 0.45) return { score: 'HIGH',   color: 'emerald', pct: Math.round(ratio * 100) };
    if (ratio >= 0.15) return { score: 'MEDIUM', color: 'yellow',  pct: Math.round(ratio * 100) };
    return                    { score: 'LOW',    color: 'red',     pct: Math.round(ratio * 100) };
  }
  if (breakdownLanguage === 'mixed') {
    if (ratio >= 0.35) return { score: 'HIGH',   color: 'emerald', pct: Math.round(ratio * 100) };
    if (ratio >= 0.10) return { score: 'MEDIUM', color: 'yellow',  pct: Math.round(ratio * 100) };
    return                    { score: 'LOW',    color: 'red',     pct: Math.round(ratio * 100) };
  }
  return null;
};

const LQS_COLORS = {
  emerald: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30',
  yellow:  'bg-yellow-500/15 text-yellow-400 border border-yellow-500/30',
  red:     'bg-red-500/15 text-red-400 border border-red-500/30',
};

const LQSBadge = ({ synopsis, breakdownLanguage }) => {
  const lqs = getLQS(synopsis, breakdownLanguage);
  if (!lqs) return null;
  return (
    <span
      title={`Language Quality: ${lqs.pct}% Malayalam script detected in synopsis (${lqs.score.toLowerCase()} match for ${breakdownLanguage} mode)`}
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded cursor-help select-none ${LQS_COLORS[lqs.color]}`}
      data-testid="lqs-badge"
    >
      <Languages className="h-2.5 w-2.5" />
      LQ {lqs.score}
    </span>
  );
};

const MasterBreakdownPage = () => {
  const { projectId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [breakdown, setBreakdown] = useState([]);
  const [remarksByScene, setRemarksByScene] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedScene, setSelectedScene] = useState(null);
  // Phase 13 (2026-07) — Master Breakdown Ask A/B/D wire-up.
  const [bimEditingItem, setBimEditingItem] = useState(null);
  const [mbAiPanelColumn, setMbAiPanelColumn] = useState(null);
  const [mbAiPanelAnchor, setMbAiPanelAnchor] = useState(null);
  const [showSceneDialog, setShowSceneDialog] = useState(false);
  const [dialogTab, setDialogTab] = useState('breakdown'); // 'breakdown' | 'script'
  const [dialogScriptText, setDialogScriptText] = useState('');
  const [dialogScriptLoading, setDialogScriptLoading] = useState(false);
  const [dialogScriptStatus, setDialogScriptStatus] = useState('ready');
  const [dialogRerunLoading, setDialogRerunLoading] = useState(false);
  const [shotList, setShotList] = useState(null);
  const [loadingShots, setLoadingShots] = useState(false);
  const [charQuery, setCharQuery] = useState('');
  const [charDropdown, setCharDropdown] = useState(false);
  const [filters, setFilters] = useState({
    location: '',
    time_of_day: '',
    character: '',
    flagged_only: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [exporting, setExporting] = useState(false);
  // ── Reanalyze state (global + per-scene) ───────────────────────────────
  const [showGlobalReanalyzeDialog, setShowGlobalReanalyzeDialog] = useState(false);
  const [globalReanalyzeInFlight, setGlobalReanalyzeInFlight] = useState(false);
  const [rerunningSceneIds, setRerunningSceneIds] = useState(() => new Set());
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingMms, setExportingMms] = useState(false);
  const [exportingLocationPdf, setExportingLocationPdf] = useState(false);
  const [exportingCastPdf, setExportingCastPdf] = useState(false);
  // ── Language dialog for PDF exports ──────────────────────────────────────
  const [showExportLangDialog, setShowExportLangDialog] = useState(false);
  const [pendingExport,        setPendingExport]        = useState(null);  // {endpoint, filename, label}
  const [synopsisLangExp,      setSynopsisLangExp]      = useState('english');  // english | original
  const [showAIImport, setShowAIImport] = useState(false);
  const [showScriptForScene, setShowScriptForScene] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [showEditScene, setShowEditScene] = useState(false);
  const [editingItemCategory, setEditingItemCategory] = useState(null);
  const [projectUsers, setProjectUsers] = useState([]);
  const [importingExcel, setImportingExcel] = useState(false);
  // ── BG Extras estimation (AI + manual) ────────────────────────────────
  const [extrasBusy, setExtrasBusy] = useState(false);
  const [showGenExtrasDialog, setShowGenExtrasDialog] = useState(false);
  const [editingExtrasSceneId, setEditingExtrasSceneId] = useState(null);
  const [editExtrasVal, setEditExtrasVal] = useState('');
  const [extrasSavedSceneId, setExtrasSavedSceneId] = useState(null);
  // Navigator-style stats strip data
  const [navCharacters, setNavCharacters] = useState([]);
  const [navScheduleDays, setNavScheduleDays] = useState([]);
  const [navScriptStatus, setNavScriptStatus] = useState(null);

  // M3.15 hash-scroll-on-mount: jump to + highlight a scene linked via #scene-<n>
  const [highlightedSceneId, setHighlightedSceneId] = useState(null);
  const hasScrolledRef = useRef(false);
  useEffect(() => {
    if (hasScrolledRef.current) return;
    if (!breakdown || breakdown.length === 0) return;
    const hash = window.location.hash;
    if (!hash || !hash.startsWith('#scene-')) return;
    const targetId = hash.slice(1); // "scene-22a"
    hasScrolledRef.current = true;
    const tryScroll = () => {
      const el = document.getElementById(targetId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedSceneId(targetId);
        setTimeout(() => setHighlightedSceneId(null), 2000);
        return true;
      }
      return false;
    };
    if (!tryScroll()) {
      requestAnimationFrame(tryScroll); // one retry
    }
  }, [breakdown]);
  const [sectionTab, setSectionTab] = useState('scenes'); // scenes|locations|cast|characters|stripboard|schedule
  const [notifications, setNotifications] = useState([]);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'table' | 'studio'
  const [activeDept, setActiveDept] = useState('ALL');
  const [showEnglishSynopsis, setShowEnglishSynopsis] = useState(false);
  const fileInputRef = useRef(null);
  const [breakdownLanguage, setBreakdownLanguage] = useState('english');
  // FIPR beat map: scene_number → stage (F/I/P/R)
  const [fiprMap, setFiprMap] = useState({});
  // AI Chat panel state
  const [showAiChat, setShowAiChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]); // [{role, content, applied, _msgId}]

  // ── NHR (Needs Human Review) modal state + helpers ────────────────────
  const [nhrScene, setNhrScene]             = useState(null);
  const [nhrLoading, setNhrLoading]         = useState(false);
  const [nhrPageNumber, setNhrPageNumber]   = useState(null);

  const openNHRModal = async (scene) => {
    setNhrScene(scene);
    setNhrPageNumber(null);
    try {
      const resp = await axios.get(
        `${API}/projects/${projectId}/script/scenes-pages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const items = resp.data?.items || [];
      const match = items.find(i => i.scene_id === scene.id);
      setNhrPageNumber(match?.page ?? null);
    } catch {
      setNhrPageNumber(null);
    }
  };

  const closeNHRModal = () => { setNhrScene(null); setNhrLoading(false); };

  const handleNHRDecision = async (action) => {
    if (!nhrScene) return;
    setNhrLoading(true);
    try {
      await axios.post(
        `${API}/scenes/${nhrScene.id}/nhr-decision`,
        { action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBreakdown(prev =>
        prev.map(s =>
          s.id === nhrScene.id
            ? { ...s, scene_text_status: action === 'approve' ? 'gemini_vision' : 'needs_human_review' }
            : s
        )
      );
      toast.success(action === 'approve' ? 'Scene text approved.' : 'Scene kept for manual review.');
      closeNHRModal();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save decision.');
      setNhrLoading(false);
    }
  };
  // Generate stable keys for chat messages (append-only list, but avoid index keys)
  const _msgIdRef = useRef(0);
  const _newMsgId = () => { _msgIdRef.current += 1; return `msg-${_msgIdRef.current}`; };
  const [chatHistoryLoaded, setChatHistoryLoaded] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);
  const chatInputRef = useRef(null);
  const correctionExamples = [
    'The character MARK is actually MARTY',
    'Rename location "Aquinas College" to "Aquinas College, Ernakulam"',
    'Add a guitar as a prop in scene 3',
    'Download PDF of scenes 1, 2, 3',
    'Export cast-wise breakdown PDF',
    'Download location-wise PDF',
  ];

  useEffect(() => {
    fetchBreakdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    checkScript();
    fetchProjectUsers();
    fetchNotifications();
    fetchFifiChatHistory();
    // Fetch project details for header
    axios.get(`${API}/projects/${projectId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setProject(r.data)).catch(() => {});
    // Navigator-style stats strip: characters, schedule, script status
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/projects/${projectId}/characters`,  { headers }).then(r => setNavCharacters(r.data || [])).catch(() => {});
    axios.get(`${API}/projects/${projectId}/schedule`,    { headers }).then(r => setNavScheduleDays(r.data || [])).catch(() => {});
    axios.get(`${API}/projects/${projectId}/script-status`,{ headers }).then(r => setNavScriptStatus(r.data)).catch(() => {});
    // FIPR beat map for scene cards
    axios.get(`${API}/projects/${projectId}/beat-board`, { headers })
      .then(r => {
        if (r.data?.status === 'ready') {
          const map = {};
          (r.data.beats || []).forEach(b => { map[b.scene_number] = b.stage; });
          setFiprMap(map);
        }
      }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFifiChatHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}/fifi-chat`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data.messages && data.messages.length > 0) {
        setChatMessages(data.messages.map((m, i) => ({ ...m, _msgId: `loaded-${i}` })));
      }
      setChatHistoryLoaded(true);
    } catch {
      setChatHistoryLoaded(true); // still mark loaded on error
    }
  };

  const fetchProjectUsers = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjectUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const checkScript = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/script-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.status === 'no_script' || response.data.status === 'processing') {
        navigate(`/projects/${projectId}/upload`);
      } else {
        setBreakdownLanguage(response.data.breakdown_language || 'english');
      }
    } catch (error) {
      console.error('Error checking script:', error);
    }
  };

  // Get unique locations from breakdown
  const uniqueLocations = [...new Set(breakdown.map(s => s.location_name))].filter(Boolean);

  const sendChatMessage = async (text) => {
    const msg = (text || chatInput).trim();
    if (!msg || chatLoading) return;

    const history = chatMessages.map(m => ({ role: m.role, content: m.content }));
    const userMsg = { role: 'user', content: msg, applied: [], _msgId: _newMsgId() };
    setChatMessages(prev => [...prev, userMsg]);
    setChatInput('');
    setChatLoading(true);

    try {
      const { data } = await axios.post(
        `${API}/projects/${projectId}/ai-chat`,
        { message: msg, history },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const aiMsg = {
        role: 'assistant',
        content: data.reply,
        applied: data.applied || [],
        _msgId: _newMsgId(),
      };
      setChatMessages(prev => [...prev, aiMsg]);
      if (data.corrections_count > 0) fetchBreakdown();
      // Execute any download actions the AI returned
      if (data.actions?.length) await executeAIActions(data.actions);
    } catch (err) {
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: `Sorry, something went wrong: ${err.response?.data?.detail || err.message}`,
        applied: [],
        _msgId: _newMsgId(),
      }]);
    } finally {
      setChatLoading(false);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      chatInputRef.current?.focus();
    }
  };

  const [translating, setTranslating] = useState(false);

  const translateAllSynopses = async () => {
    setTranslating(true);
    // Add a "user" message to the chat
    const userMsg = { role: 'user', content: 'Translate all synopses to English', applied: [], _msgId: _newMsgId() };
    setChatMessages(prev => [...prev, userMsg]);
    try {
      const { data } = await axios.post(
        `${API}/projects/${projectId}/translate-synopses`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const aiMsg = {
        role: 'assistant',
        content: data.message,
        applied: data.translated > 0
          ? [`Translated ${data.translated} synopsis(es) from non-English to English`]
          : [],
        _msgId: _newMsgId(),
      };
      setChatMessages(prev => [...prev, aiMsg]);
      if (data.translated > 0) fetchBreakdown();
    } catch (err) {
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: `Translation failed: ${err.response?.data?.detail || err.message}`,
        applied: [],
        _msgId: _newMsgId(),
      }]);
    } finally {
      setTranslating(false);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  const openAiChat = () => {
    setShowAiChat(true);
    if (chatHistoryLoaded && chatMessages.length === 0) {
      setChatMessages([{
        role: 'assistant',
        content: "Hi! I'm **Fifi AI**, your breakdown assistant.\nTell me what to fix or what to download — you can type in English or Malayalam.\n\n• *The character MARK is actually MARTY*\n• *Download PDF of scenes 1, 5, 10*\n• *Export cast-wise breakdown as PDF*\n• *Add a guitar as a prop in scene 3*",
        applied: [],
        _msgId: 'greeting',
      }]);
    }
    setTimeout(() => {
      chatInputRef.current?.focus();
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 200);
  };

  const fetchBreakdown = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.location && filters.location !== 'all') params.append('location', filters.location);
      if (filters.time_of_day && filters.time_of_day !== 'all') params.append('time_of_day', filters.time_of_day);
      if (filters.character && filters.character !== 'all') params.append('character', filters.character);
      if (filters.flagged_only) params.append('flagged_only', 'true');

      const response = await axios.get(`${API}/projects/${projectId}/breakdown?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBreakdown(response.data);
      // Fetch remarks (internal notes from Strip Board / Schedule) — never exported, only shown in master sheet
      try {
        const remarksRes = await axios.get(`${API}/projects/${projectId}/remarks`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRemarksByScene(remarksRes.data || {});
      } catch { /* noop */ }
    } catch (error) {
      toast.error('Failed to load breakdown');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, token, filters]);

  // ── Reanalyze handlers ─────────────────────────────────────────────────
  // Global: reprocess the whole script. Per-scene: refresh just this one
  // scene's breakdown_items via POST /api/scenes/{scene_id}/breakdown/rerun.
  const handleGlobalReanalyze = useCallback(async () => {
    if (globalReanalyzeInFlight) return;
    setGlobalReanalyzeInFlight(true);
    try {
      await axios.post(
        `${API}/projects/${projectId}/reanalyze-script`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Re-analyzing script — this may take a few minutes.');
      setShowGlobalReanalyzeDialog(false);
      // Send the user to the upload/processing page so they can watch progress.
      navigate(`/projects/${projectId}/upload`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 402 || detail?.error === 'LLM_BUDGET_EXCEEDED') {
        toast.error('AI generation limit reached. Please try again later.');
      } else {
        toast.error(detail?.message || detail || 'Failed to start re-analysis');
      }
    } finally {
      setGlobalReanalyzeInFlight(false);
    }
  }, [projectId, token, navigate, globalReanalyzeInFlight]);

  const handleSceneReanalyze = useCallback(async (sceneId) => {
    if (!sceneId || rerunningSceneIds.has(sceneId)) return;
    setRerunningSceneIds(prev => {
      const next = new Set(prev);
      next.add(sceneId);
      return next;
    });
    try {
      await axios.post(
        `${API}/scenes/${sceneId}/breakdown/rerun`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Scene reanalyzed');
      // Refresh the full breakdown so the chips on this scene card update.
      // (Single-scene patch is possible too, but this keeps the optimistic
      // surface area minimal and matches the global-refresh fallback.)
      await fetchBreakdown();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 402 || detail?.error === 'LLM_BUDGET_EXCEEDED') {
        toast.error('AI generation limit reached. Please try again later.');
      } else {
        toast.error(detail?.message || detail || 'Failed to reanalyze scene');
      }
    } finally {
      setRerunningSceneIds(prev => {
        const next = new Set(prev);
        next.delete(sceneId);
        return next;
      });
    }
  }, [token, fetchBreakdown, rerunningSceneIds]);

  const generateShotList = useCallback(async (sceneId) => {
    setLoadingShots(true);
    setShotList(null);
    try {
      const res = await axios.post(
        `${API}/projects/${projectId}/scenes/${sceneId}/shot-list`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShotList(res.data.shots || []);
    } catch {
      toast.error('Failed to generate shot list');
    } finally {
      setLoadingShots(false);
    }
  }, [projectId, token]);
  const updateSceneField = useCallback(async (sceneId, field, value) => {
    // Optimistic local update
    setBreakdown(prev => prev.map(s => s.id === sceneId ? { ...s, [field]: value } : s));
    if (selectedScene?.id === sceneId) setSelectedScene(prev => prev ? { ...prev, [field]: value } : prev);
    try {
      const res = await axios.patch(
        `${API}/projects/${projectId}/scenes/${sceneId}/field`,
        { field, value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Show toast when notes @mention created Slate tasks
      if (field === 'notes' && res.data?._tasks_created) {
        toast.success(`${res.data._tasks_created} Slate task${res.data._tasks_created > 1 ? 's' : ''} created from note tags`);
      }
    } catch (err) {
      toast.error('Failed to save change');
      fetchBreakdown(); // revert on error
    }
  }, [projectId, token, selectedScene]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateItemField = useCallback(async (itemId, sceneId, field, value) => {
    // Optimistic: update item inside scene breakdown_items
    setBreakdown(prev => prev.map(s => {
      if (s.id !== sceneId) return s;
      const items = { ...s.breakdown_items };
      for (const cat of Object.keys(items)) {
        items[cat] = items[cat].map(it => it.id === itemId ? { ...it, [field]: value } : it);
      }
      return { ...s, breakdown_items: items };
    }));
    try {
      await axios.patch(
        `${API}/projects/${projectId}/breakdown-items/${itemId}/field`,
        { field, value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch {
      toast.error('Failed to save change');
      fetchBreakdown();
    }
  }, [projectId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Entity management ──────────────────────────────────────────────── */
  const [entityList, setEntityList] = useState([]);

  const refreshEntities = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/entities`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const entities = res.data || [];
      setEntityList(entities);

      // Auto-sync: if no entities found, run migration then re-fetch
      // This ensures Locations/Characters appear immediately on first visit
      if (entities.length === 0) {
        try {
          await axios.post(`${API}/projects/${projectId}/entities/migrate`, {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const res2 = await axios.get(`${API}/projects/${projectId}/entities`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setEntityList(res2.data || []);
        } catch { /* silent — manual sync still available */ }
      }
    } catch { /* silent */ }
  }, [projectId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (sectionTab === 'locations' || sectionTab === 'characters') {
      refreshEntities();
    }
  }, [sectionTab, refreshEntities]);

  const handleUpdateEntity = useCallback(async (entityId, data) => {
    if (data._deleted || entityId === '__refresh__') {
      await refreshEntities();
      await fetchBreakdown();
      return;
    }
    try {
      const res = await axios.patch(
        `${API}/projects/${projectId}/entities/${entityId}`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Optimistic entity update
      setEntityList(prev => prev.map(e => e.id === entityId ? { ...e, ...data, ...(res.data || {}) } : e));
      // If name changed, refresh scenes
      if (data.name && res.data?.scenes_affected?.length > 0) {
        toast.success(`Renamed in ${res.data.scenes_affected.length} scene(s)`);
        fetchBreakdown();
      }
    } catch {
      toast.error('Failed to update entity');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, token, refreshEntities, fetchBreakdown]);

  const handleRunMigration = useCallback(async () => {
    try {
      const res = await axios.post(
        `${API}/projects/${projectId}/entities/migrate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Synced: ${res.data.migrated?.locations || 0} locations, ${res.data.migrated?.characters || 0} characters, ${res.data.migrated?.items || 0} items`);
      await refreshEntities();
    } catch {
      toast.error('Sync failed');
    }
  }, [projectId, token, refreshEntities]);

  // ── Shared PDF download helper — asks language preference before exporting ──
  const openExportDialog = (endpoint, filename, label) => {
    setPendingExport({ endpoint, filename, label });
    setShowExportLangDialog(true);
  };

  const confirmExport = async () => {
    if (!pendingExport) return;
    setShowExportLangDialog(false);
    const { endpoint, filename } = pendingExport;
    setPendingExport(null);
    try {
      const url = `${API}/projects/${projectId}/${endpoint}?synopsis_lang=${synopsisLangExp}`;
      const r = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = blobUrl; a.setAttribute('download', filename);
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('PDF exported!');
    } catch {
      toast.error('Failed to export PDF');
    }
  };

  const handleExportPdf = () => openExportDialog('export/pdf', `breakdown_book_${projectId}.pdf`, 'Breakdown Book');
  const handleExportLocationPdf = () => openExportDialog('export/location-wise', `location_wise_${projectId}.pdf`, 'Location-Wise PDF');
  const handleExportCastPdf = () => openExportDialog('export/cast-wise', `cast_wise_${projectId}.pdf`, 'Cast-Wise PDF');

  // ── BG Extras estimation ─────────────────────────────────────────────
  const canRunExtrasGen = user?.global_role === 'superadmin' || user?.role === 'PRODUCER';

  const handleGenerateExtras = async () => {
    setShowGenExtrasDialog(false);
    setExtrasBusy(true);
    try {
      const r = await axios.post(
        `${API}/projects/${projectId}/extras/generate-estimates`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`${r.data.estimated} scenes estimated, ${r.data.skipped} skipped`);
      await fetchBreakdown();
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error(err.response.data?.detail || 'AI generation limit reached');
      } else if (err.response?.status === 403) {
        toast.error('Permission denied');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to generate estimates');
      }
    } finally {
      setExtrasBusy(false);
    }
  };

  const handleSaveExtrasEstimate = async (sceneId, count) => {
    const n = parseInt(count, 10);
    if (Number.isNaN(n) || n < 0) {
      toast.error('Count must be a non-negative integer');
      return;
    }
    try {
      const r = await axios.patch(
        `${API}/projects/${projectId}/scenes/${sceneId}/extras-estimate`,
        { count: n },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Optimistic patch
      setBreakdown(prev => prev.map(s =>
        s.id === sceneId ? { ...s, extras_estimate: r.data.extras_estimate } : s
      ));
      setEditingExtrasSceneId(null);
      setExtrasSavedSceneId(sceneId);
      setTimeout(() => setExtrasSavedSceneId(curr => curr === sceneId ? null : curr), 1200);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save estimate');
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `breakdown_${projectId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Breakdown exported successfully');
    } catch (error) {
      toast.error('Failed to export breakdown');
    } finally {
      setExporting(false);
    }
  };

  const handleExportMms = async () => {
    setExportingMms(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/export/mms`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mms_schedule_${projectId}.tsv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('MMS schedule exported!');
    } catch (error) {
      toast.error('Failed to export MMS schedule');
    } finally {
      setExportingMms(false);
    }
  };

  // ── New: Location-wise PDF — uses language dialog ─────────────────────────
  const handleExportLocationWise = () =>
    openExportDialog('export/location-wise', 'location_wise.pdf', 'Location-Wise PDF');

  // ── New: Cast-wise PDF — uses language dialog ──────────────────────────────
  const handleExportCastWise = () =>
    openExportDialog('export/cast-wise', 'cast_wise.pdf', 'Cast-Wise PDF');

  // ── Fifi AI: download specific scenes PDF ─────────────────────────────────
  const handleExportScenesPdf = async (sceneNumbers) => {
    try {
      const response = await axios.post(
        `${API}/projects/${projectId}/export/scenes-pdf`,
        { scene_numbers: sceneNumbers },
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const cd = response.headers['content-disposition'] || '';
      const fname = cd.match(/filename="?([^"]+)"?/)?.[1] || 'selected_scenes.pdf';
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a'); a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      toast.success(`PDF ready — scenes: ${sceneNumbers.slice(0, 5).join(', ')}${sceneNumbers.length > 5 ? '…' : ''}`);
    } catch { toast.error('Scenes PDF export failed'); }
  };

  // ── Execute AI download actions returned from Fifi ─────────────────────────
  const executeAIActions = async (actions) => {
    if (!actions?.length) return;
    for (const action of actions) {
      if (action.type === 'download_scenes_pdf' && action.scene_numbers?.length) {
        await handleExportScenesPdf(action.scene_numbers);
      } else if (action.type === 'download_location_pdf') {
        await handleExportLocationWise();
      } else if (action.type === 'download_cast_pdf') {
        await handleExportCastWise();
      }
    }
  };



  const handleExportOneliner = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/export/oneliner?synopsis_lang=${synopsisLangExp}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `oneliner_${projectId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('One-Liner Schedule exported!');
    } catch (error) {
      toast.error('Failed to export one-liner');
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImportingExcel(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/projects/${projectId}/import-excel`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(response.data.message);
      fetchBreakdown();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import Excel');
    } finally {
      setImportingExcel(false);
      e.target.value = ''; // Reset
    }
  };

  const fetchComments = async (sceneId) => {
    try {
      const response = await axios.get(`${API}/scenes/${sceneId}/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handlePostComment = async () => {
    if (!newComment.trim() || !selectedScene) return;
    setPostingComment(true);
    try {
      // Parse @mentions
      const mentionMatches = newComment.match(/@(\w+(?:\s\w+)?)/g) || [];
      const mentions = [];
      mentionMatches.forEach(m => {
        const name = m.substring(1).toLowerCase();
        const matchedUser = projectUsers.find(u => 
          u.full_name.toLowerCase().includes(name) || u.email.toLowerCase().startsWith(name)
        );
        if (matchedUser) mentions.push(matchedUser.id);
      });
      
      await axios.post(`${API}/scenes/${selectedScene.id}/comments`, {
        content: newComment,
        mentions
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewComment('');
      fetchComments(selectedScene.id);
      toast.success('Comment posted');
    } catch (error) {
      toast.error('Failed to post comment');
    } finally {
      setPostingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (selectedScene) fetchComments(selectedScene.id);
      toast.success('Comment deleted');
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const viewSceneDetail = async (sceneId) => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/scenes/${sceneId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedScene(response.data);
      setShowSceneDialog(true);
      setDialogTab('breakdown');
      setDialogScriptText('');
      setDialogScriptStatus('loading');
      setShotList(null);
      setCharQuery('');
      fetchComments(sceneId);
      // Lazy-fetch screenplay; poll while backend extracts via Gemini Vision
      setDialogScriptLoading(true);
      pollSceneScreenplay(sceneId);
    } catch (error) {
      toast.error('Failed to load scene details');
    }
  };

  // Reusable poll loop for /screenplay — used both on initial scene open
  // and after an explicit "Re-analyze scene" click from the needs_reextract
  // empty state. Polls while status === 'processing', capped at 30 attempts
  // (~90 s). Any terminal status stops the loop and settles the UI.
  const pollSceneScreenplay = async (sceneId, attempt = 0) => {
    try {
      const sr = await axios.get(
        `${API}/scenes/${sceneId}/screenplay`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newText = sr.data?.scene_text || '';
      const newStatus = sr.data?.status || 'ready';
      setDialogScriptText(newText);
      setDialogScriptStatus(newStatus);
      if (newStatus === 'processing' && attempt < 30) {
        setTimeout(() => pollSceneScreenplay(sceneId, attempt + 1), 3000);
      } else {
        setDialogScriptLoading(false);
      }
    } catch {
      setDialogScriptStatus('error');
      setDialogScriptLoading(false);
    }
  };

  // User-triggered Vision re-extract for scenes that landed in the
  // needs_reextract state (backend status: awaiting_vision_reextract).
  // Fires POST /api/scenes/{id}/screenplay/rerun then resumes polling.
  const triggerSceneRerun = async (sceneId) => {
    if (!sceneId || dialogRerunLoading) return;
    setDialogRerunLoading(true);
    try {
      await axios.post(
        `${API}/scenes/${sceneId}/screenplay/rerun`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Reset UI to loading + processing, then reuse the poll loop.
      setDialogScriptText('');
      setDialogScriptStatus('processing');
      setDialogScriptLoading(true);
      pollSceneScreenplay(sceneId);
      toast.success('Re-analysing scene…');
    } catch (e) {
      toast.error('Failed to start scene re-analysis');
      setDialogScriptLoading(false);
    } finally {
      setDialogRerunLoading(false);
    }
  };

  const getCategoryBadgeColor = (category) => {
    // Legacy compatibility — returns Tailwind classes for older usages
    const colors = {
      PROP: 'bg-blue-500/20 text-blue-400',
      COSTUME: 'bg-yellow-500/20 text-yellow-400',
      ART_SET_DRESSING: 'bg-purple-500/20 text-purple-400',
      VFX: 'bg-cyan-500/20 text-cyan-400',
      STUNT: 'bg-orange-500/20 text-orange-400',
      SFX_SPECIAL_EFFECTS: 'bg-red-500/20 text-red-400',
      MAKEUP_HAIR: 'bg-pink-500/20 text-pink-400',
      SOUND_MUSIC: 'bg-teal-500/20 text-teal-400',
      VEHICLE: 'bg-amber-500/20 text-amber-400',
      PICTURE_VEHICLE: 'bg-rose-500/20 text-rose-400',
      ANIMAL: 'bg-green-500/20 text-green-400',
      EXTRA_BACKGROUND: 'bg-lime-500/20 text-lime-400',
    };
    return colors[category] || 'bg-gray-500/20 text-gray-400';
  };

  // Minimal monochrome chip renderer
  const renderCategoryChip = (category, count) => {
    const cfg = CAT_CHIPS[category] || { label: category.replace(/_/g, ' '), color: DT.textDim };
    return (
      <span
        key={category}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold"
        style={{ background: 'rgba(255,255,255,0.06)', color: cfg.color, border: '1px solid rgba(255,255,255,0.08)' }}
      >
        {cfg.label} {count > 0 && <span style={{ opacity: 0.6 }}>({count})</span>}
      </span>
    );
  };

  const filteredBreakdown = breakdown.filter(scene => 
    searchTerm === '' || 
    String(scene.scene_number ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    scene.scene_header.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (scene.synopsis || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Returns the synopsis to display based on the language toggle
  const getSynopsis = (scene) => {
    if (showEnglishSynopsis && scene.synopsis_en) return scene.synopsis_en;
    return scene.synopsis || '';
  };

  // Breakdown items occasionally arrive as a Python-dict repr like
  //   "{'text': 'ഫം ഷർ...', 'en': 'fishing boat', 'confidence': 1.0}"
  // (Parser AI dual-field output stringified). We delegate to the
  // canonical helper which:
  //   - parses dict-strings
  //   - prefers Malayalam `text` for original mode
  //   - falls back to `en` if `text` shows PDF-CID corruption markers
  //     (# $ ?, Latin adjacent to Malayalam, etc.)
  //   - returns clean English when the toggle is set to English
  const getItemText = (value) =>
    getBreakdownItemLabel(value, showEnglishSynopsis ? 'english' : 'original');

  // Department to categories mapping
  const DEPT_CATEGORIES = {
    ALL: null, // show all
    ART: ['PROP', 'ART_SET_DRESSING', 'PICTURE_VEHICLE'],
    COSTUME: ['COSTUME'],
    CASTING: null, // characters list
    MAKEUP: ['MAKEUP_HAIR'],
    PRODUCTION: null, // shows everything + scene info
    OTHERS: ['VFX', 'STUNT', 'SFX_SPECIAL_EFFECTS', 'VEHICLE', 'ANIMAL', 'EXTRA_BACKGROUND', 'SOUND_MUSIC', 'PRODUCTION_NOTE']
  };

  // Filter scene's breakdown items by active department
  const filterItemsByDept = (breakdownItems) => {
    if (activeDept === 'ALL' || activeDept === 'PRODUCTION') return breakdownItems;
    const allowedCats = DEPT_CATEGORIES[activeDept];
    if (!allowedCats) return breakdownItems;
    const filtered = {};
    Object.entries(breakdownItems || {}).forEach(([cat, items]) => {
      if (allowedCats.includes(cat)) filtered[cat] = items;
    });
    return filtered;
  };

  return (
    <>
    <div className="min-h-screen bg-[#0d0d0d]" data-testid="master-breakdown-page">

      {/* ── Cinematic Production Header ─────────────────────────────────────── */}
      {(() => {
        const allChars = new Set(breakdown.flatMap(s => s.characters_in_scene || []));
        const allLocs  = new Set(breakdown.map(s => s.location_name).filter(Boolean));
        const flagged  = breakdown.filter(s => s.is_flagged).length;
        const extCount = breakdown.filter(s => (s.location_type || '').toUpperCase() === 'EXT').length;
        return (
          <div style={{ background: DT.surface, borderBottom: `1px solid ${DT.border}` }} className="px-6 py-4">
            <div className="max-w-7xl mx-auto">
              <ScriptHealthBanner projectId={projectId} onRestored={() => window.location.reload()} />
            </div>
            <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
              {/* Left: project title + meta */}
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <button onClick={() => navigate('/dashboard')} className="text-[10px] font-semibold uppercase tracking-widest transition-colors" style={{ color: DT.accent, letterSpacing: '0.12em' }} data-testid="back-to-dashboard-btn">
                    ← Dashboard
                  </button>
                </div>
                <h1 className="font-semibold text-2xl leading-tight" style={{ color: DT.textPri }}>
                  {project?.title || 'Master Breakdown'}
                </h1>
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  {(() => {
                    const sceneCounts = countSceneNumbers(breakdown);
                    const scenesLabel = sceneCounts.unnumbered > 0
                      ? `${sceneCounts.numbered} numbered + ${sceneCounts.unnumbered} unnumbered`
                      : null;
                    return [
                      { v: breakdown.length, l: 'Scenes', suffix: scenesLabel },
                      { v: allLocs.size, l: 'Locations' },
                      { v: allChars.size, l: 'Cast' },
                      { v: extCount, l: 'EXT' },
                    ].map(({ v, l, suffix }) => (
                      <span key={l} className="text-[10px]" style={{ color: DT.textDim }}>
                        <span className="font-bold tabular-nums" style={{ color: DT.textSec }}>{v}</span> {l}
                        {suffix && (
                          <span className="ml-1 text-[9px] opacity-70" data-testid="scenes-unnumbered-suffix">({suffix})</span>
                        )}
                      </span>
                    ));
                  })()}
                  {project?.genre && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: DT.accentDim, color: DT.accent }}>{project.genre}</span>
                  )}
                  {flagged > 0 && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(248,113,113,0.1)', color: '#F87171' }}>
                      {flagged} Flagged
                    </span>
                  )}
                </div>
              </div>

              {/* Right: action buttons */}
              <div className="flex gap-2 flex-wrap" data-testid="toolbar-buttons">
                <Button onClick={() => navigate(`/projects/${projectId}/share`)} variant="outline" size="sm" className="border-[var(--cb-border)] text-[var(--cb-text)] hover:bg-[var(--cb-surface)]" data-testid="share-btn">
                  <Share2 className="h-4 w-4 mr-1" /><span className="hidden sm:inline">Share</span>
                </Button>
                <Button onClick={() => navigate(`/projects/${projectId}/versions`)} variant="outline" size="sm" className="border-[var(--cb-border)] text-[var(--cb-text)] hover:bg-[var(--cb-surface)]" data-testid="versions-btn">
                  <Camera className="h-4 w-4 mr-1" /><span className="hidden sm:inline">Versions</span>
                </Button>
                <Button
                  onClick={() => setShowGlobalReanalyzeDialog(true)}
                  disabled={globalReanalyzeInFlight}
                  variant="outline"
                  size="sm"
                  className="border-[var(--cb-border)] text-[var(--cb-text)] hover:bg-[var(--cb-surface)]"
                  data-testid="reanalyze-script-btn"
                  title="Re-run AI analysis on the entire script"
                >
                  {globalReanalyzeInFlight ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-1" />
                  )}
                  <span className="hidden sm:inline">Reanalyze Script</span>
                </Button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImportExcel}
              accept=".xlsx"
              className="hidden"
              data-testid="import-excel-input"
            />
            {/* Unified Import Excel (AI-powered) — replaces separate Import Excel + AI Import buttons */}
            <Button
              onClick={() => setShowAIImport(true)}
              variant="outline"
              size="sm"
              className="border-[#F5A623]/40 text-[#F5A623] hover:bg-[#F5A623]/10"
              data-testid="import-excel-btn"
              title="Import Excel — AI-powered, works with any format & column naming"
            >
              <Sparkles className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Import Excel</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  className="bg-[#F5A623] hover:bg-[#d6b539] text-black font-medium"
                  data-testid="export-dropdown-btn"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[var(--cb-surface)] border-[var(--cb-border)] text-[var(--cb-text)]">
                <DropdownMenuItem 
                  onClick={handleExportExcel}
                  className="cursor-pointer hover:bg-[var(--cb-surface-h)]"
                  data-testid="export-excel-option"
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  {exporting ? 'Exporting...' : 'Excel Spreadsheet'}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={handleExportPdf}
                  className="cursor-pointer hover:bg-[var(--cb-surface-h)]"
                  data-testid="export-pdf-option"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  {exportingPdf ? 'Exporting...' : 'PDF Breakdown Book'}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={handleExportOneliner}
                  className="cursor-pointer hover:bg-[var(--cb-surface-h)]"
                  data-testid="export-oneliner-option"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  One Liner Script (PDF)
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[#2a2a2a]" />
                <DropdownMenuItem 
                  onClick={handleExportMms}
                  className="cursor-pointer hover:bg-[var(--cb-surface-h)]"
                  data-testid="export-mms-option"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {exportingMms ? 'Exporting...' : 'Movie Magic Schedule'}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[#2a2a2a]" />
                <DropdownMenuItem
                  onClick={handleExportLocationWise}
                  className="cursor-pointer hover:bg-[var(--cb-surface-h)]"
                  data-testid="export-location-option"
                >
                  <MapPin className="h-4 w-4 mr-2 text-[#4a9eff]" />
                  {exportingLocationPdf ? 'Exporting...' : 'Location-Wise Breakdown (PDF)'}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleExportCastWise}
                  className="cursor-pointer hover:bg-[var(--cb-surface-h)]"
                  data-testid="export-cast-option"
                >
                  <Users className="h-4 w-4 mr-2 text-[#34D399]" />
                  {exportingCastPdf ? 'Exporting...' : 'Cast-Wise Scene Breakdown (PDF)'}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[#2a2a2a]" />
                <DropdownMenuItem
                  onClick={async () => {
                    try {
                      toast.info('Extracting background actors with AI…');
                      const res = await axios.post(`${API}/projects/${projectId}/extract-background-actors`, {}, { headers: { Authorization: `Bearer ${token}` } });
                      toast.success(res.data.message);
                      fetchBreakdown();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Extraction failed');
                    }
                  }}
                  className="cursor-pointer hover:bg-[var(--cb-surface-h)]"
                  data-testid="extract-bg-actors-btn"
                >
                  <Wand2 className="h-4 w-4 mr-2 text-[#a3e635]" />
                  AI Extract Background Actors
                </DropdownMenuItem>

              </DropdownMenuContent>
            </DropdownMenu>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Navigator-style stats strip + Quick actions + View Toggle */}
        {(() => {
          const totalScenes  = breakdown.length;
          // Pages: prefer real PDF page count (PyPDF2); fall back to summed eighths.
          const eighthsSum   = breakdown.reduce((s, sc) => s + ((sc.page_count_eighths || 0) / 8), 0);
          const totalPages   = (navScriptStatus?.pdf_total_pages > 0)
            ? `${navScriptStatus.pdf_total_pages}`
            : `${eighthsSum.toFixed(1)}p`;
          // Use the SAME sources as the page-header pill counters
          const locationsSet = new Set(breakdown.map(sc => sc.location_name).filter(Boolean));
          const totalLocs    = locationsSet.size;
          const castSet      = new Set(breakdown.flatMap(sc => sc.characters_in_scene || []));
          const totalCast    = castSet.size;
          // Shoot days: only "filled" when EVERY scene is scheduled; otherwise TBD
          const hasSchedule  = (navScheduleDays || []).length > 0;
          const scheduledSceneIds = new Set(
            (navScheduleDays || []).flatMap(d => (d.scene_ids || d.scenes || []).map(x =>
              typeof x === 'string' ? x : (x?.id || x?.scene_id)
            ))
          );
          const allScheduled = totalScenes > 0 && scheduledSceneIds.size >= totalScenes;
          const totalDays    = allScheduled ? navScheduleDays.length : 'TBD';
          const daysTooltip  = allScheduled
            ? `${navScheduleDays.length} shoot day${navScheduleDays.length === 1 ? '' : 's'} (all ${totalScenes} scenes scheduled)`
            : hasSchedule
              ? `Partial schedule: ${scheduledSceneIds.size} of ${totalScenes} scenes scheduled — finish scheduling all scenes to lock the shoot-day count`
              : 'To Be Decided — no scenes scheduled yet';
          // Script version label — user-supplied at upload, else fallback v{n}
          const scriptVer    = navScriptStatus?.version_label
            || (navScriptStatus?.version_number ? `v${navScriptStatus.version_number}` : 'v—');

          // Extraction method display — user-facing labels only (no library
          // names). See dispatch B, bundled fix 2 (2026-02).
          const EXTRACT_META = {
            pypdf2_direct:      { label: 'Standard Text',           color: '#4ade80', desc: 'Standard PDF with embedded text.' },
            pypdf2_indic_clean: { label: 'Multilingual Text',       color: '#60a5fa', desc: 'Indic-script PDF with clean embedded text.' },
            pypdf2_cid_corrupt: { label: 'AI Vision Read',          color: '#F5A623', desc: 'Scene text read from rendered PDF pages by AI vision.' },
            pypdf2_failed:      { label: 'Scanned PDF — Needs review', color: '#f87171', desc: 'Very little text extracted. PDF may be image-based.' },
            plain_text:         { label: 'Plain Text File',         color: '#a78bfa', desc: 'Non-PDF: text read directly.' },
          };
          const exMethod = navScriptStatus?.extraction_method;
          const exMeta   = EXTRACT_META[exMethod] || null;
          return (
            <div className="mb-5" data-testid="navigator-stats-block">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <NavStat label="Scenes"         value={totalScenes}             color="#F5A623" Icon={Film}         testid="stat-scenes" tooltip={(() => { const c = countSceneNumbers(breakdown); return c.unnumbered > 0 ? `${c.numbered} numbered + ${c.unnumbered} unnumbered` : undefined; })()} />
                <NavStat label="Script Pages"   value={totalPages} color="#facc15" Icon={FileText}     testid="stat-pages" />
                <NavStat label="Locations"      value={totalLocs}  color="#60a5fa" Icon={MapPin}       testid="stat-locations" />
                <NavStat label="Cast"           value={totalCast}  color="#4ade80" Icon={Users}        testid="stat-cast" />
                <NavStat label="Script Version" value={scriptVer}               color="#c084fc" Icon={Layers}       testid="stat-version" />
              </div>
              {/* Extraction method indicator — hidden for AI-vision path (handled automatically) */}
              {exMeta && exMethod !== 'pypdf2_cid_corrupt' && (
                <div className="mt-3 flex items-center justify-between gap-2 px-3 py-2 rounded-lg flex-wrap"
                     style={{ background: `${exMeta.color}0d`, border: `1px solid ${exMeta.color}30` }}
                     data-testid="extraction-method-badge"
                     title={exMeta.desc}>
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: exMeta.color }} />
                    <span className="text-[11px] font-semibold" style={{ color: exMeta.color }}>
                      PDF Extraction:
                    </span>
                    <span className="text-[11px]" style={{ color: 'var(--cb-text-sec)' }}>
                      {exMeta.label}
                    </span>
                  </div>
                </div>
              )}
              {/* quick-action row removed */}
            </div>
          );
        })()}

        {/* Filters — below quick-action buttons, above section tabs */}
        {sectionTab === 'scenes' && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4" data-testid="filters-card">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[var(--cb-text-sec)]" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search scenes..."
                className="pl-10 border-[var(--cb-border)] text-[var(--cb-text)]"
                style={{ background: 'var(--cb-bg)' }}
                data-testid="search-input"
              />
            </div>
            <Select value={filters.location || 'all'} onValueChange={(v) => setFilters({...filters, location: v})}>
              <SelectTrigger className="border-[var(--cb-border)] text-[var(--cb-text)]" style={{ background: 'var(--cb-bg)' }} data-testid="location-filter">
                <SelectValue placeholder="All Locations" />
              </SelectTrigger>
              <SelectContent className="bg-[var(--cb-surface)] border-[var(--cb-border)] text-[var(--cb-text)] max-h-72 overflow-y-auto">
                <SelectItem value="all">All Locations</SelectItem>
                {entityList.filter(e => e.type === 'LOCATION').sort((a,b) => (b.scene_count||0)-(a.scene_count||0)).map(e => (
                  <SelectItem key={e.id} value={e.name}>
                    <span className="flex items-center gap-2">
                      <span className="font-medium">{e.name}</span>
                      <span className="text-[10px] opacity-50">{e.scene_count || 0} scenes</span>
                    </span>
                  </SelectItem>
                ))}
                {entityList.filter(e => e.type === 'LOCATION').length === 0 && uniqueLocations.map(loc => (
                  <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.time_of_day || 'all'} onValueChange={(v) => setFilters({...filters, time_of_day: v})}>
              <SelectTrigger className="border-[var(--cb-border)] text-[var(--cb-text)]" style={{ background: 'var(--cb-bg)' }} data-testid="time-filter">
                <SelectValue placeholder="Day/Night" />
              </SelectTrigger>
              <SelectContent className="bg-[var(--cb-surface)] border-[var(--cb-border)] text-[var(--cb-text)]">
                <SelectItem value="all">All Times</SelectItem>
                <SelectItem value="DAY">Day</SelectItem>
                <SelectItem value="NIGHT">Night</SelectItem>
                <SelectItem value="DAWN">Dawn</SelectItem>
                <SelectItem value="DUSK">Dusk</SelectItem>
                <SelectItem value="CONTINUOUS">Continuous</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant={filters.flagged_only ? "default" : "outline"}
              onClick={() => setFilters({...filters, flagged_only: !filters.flagged_only})}
              className={filters.flagged_only ? "bg-[#F5A623] text-black" : "border-[var(--cb-border)] text-[var(--cb-text)]"}
              data-testid="flagged-filter-btn"
            >
              <Flag className="h-4 w-4 mr-2" />
              Flagged Only
            </Button>
          </div>
        )}

        {/* View Toggle */}
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          {/* Left: Section tab strip (Navigator-style — switches view in place) */}
          <div className="flex items-center gap-0.5 bg-[var(--cb-surface)] border border-[var(--cb-border)] rounded-lg p-1 overflow-x-auto" data-testid="nav-section-tabs">
            {[
              { id: 'scenes',      label: 'Scenes',      Icon: Film },
              { id: 'locations',   label: 'Locations',   Icon: MapPin },
              { id: 'characters',  label: 'Characters',  Icon: Users },
              { id: 'extras',      label: 'Extras',      Icon: Package },
              { id: 'stripboard',  label: 'Strip Board', Icon: GripVertical },
              { id: 'schedule',    label: 'Schedule',    Icon: CalendarDays },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setSectionTab(t.id)}
                data-testid={`section-tab-${t.id}`}
                className={`h-7 px-3 rounded-md text-xs font-semibold flex items-center gap-1.5 whitespace-nowrap transition-colors ${
                  sectionTab === t.id
                    ? 'bg-[#F5A623] text-black'
                    : 'text-[var(--cb-text-sec)] hover:bg-[var(--cb-surface)]'
                }`}
              >
                <t.Icon className="h-3.5 w-3.5" />{t.label}
              </button>
            ))}
          </div>

          {/* Right: existing view toggle */}
          <div className="flex items-center gap-2">
            {/* Synopsis language toggle — only shown for non-English breakdowns */}
            {breakdownLanguage !== 'english' && (
              <button
                data-testid="synopsis-lang-toggle"
                onClick={() => setShowEnglishSynopsis(p => !p)}
                title={showEnglishSynopsis ? 'Showing English — click for original language' : 'Showing original language — click for English'}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all duration-150 ${
                  showEnglishSynopsis
                    ? 'bg-[#4a9eff]/15 border-[#4a9eff]/40 text-[#4a9eff]'
                    : 'bg-[#e8c547]/10 border-[#e8c547]/40 text-[#e8c547]'
                }`}
              >
                <Languages className="h-3.5 w-3.5" />
                {showEnglishSynopsis ? 'EN Synopsis' : 'Original Synopsis'}
              </button>
            )}
            <div className="flex gap-1 bg-[var(--cb-surface)] border border-[var(--cb-border)] rounded-lg p-1">
              <Button size="sm" variant={viewMode === 'studio' ? 'default' : 'ghost'} onClick={() => setViewMode('studio')}
                className={viewMode === 'studio' ? 'bg-[#F5A623] text-black h-7 text-xs' : 'text-[var(--cb-text-sec)] h-7 text-xs'}
                data-testid="view-studio-btn" title="Studio — 3-panel command center">
                <LayoutGrid className="h-3.5 w-3.5 mr-1" />Studio
              </Button>
              <Button size="sm" variant={viewMode === 'cards' ? 'default' : 'ghost'} onClick={() => setViewMode('cards')}
                className={viewMode === 'cards' ? 'bg-[#F5A623] text-black h-7 text-xs' : 'text-[var(--cb-text-sec)] h-7 text-xs'}
                data-testid="view-cards-btn">
                Cards
              </Button>
              <Button size="sm" variant={viewMode === 'table' ? 'default' : 'ghost'} onClick={() => setViewMode('table')}
                className={viewMode === 'table' ? 'bg-[#F5A623] text-black h-7 text-xs' : 'text-[var(--cb-text-sec)] h-7 text-xs'}
                data-testid="view-table-btn">
                Excel
              </Button>
            </div>
          </div>
        </div>


        {/* Breakdown Display */}
        {sectionTab === 'locations' ? (
          <Card className="bg-[#141414] border-[#232323] p-6" data-testid="entity-locations-panel">
            <EntityPanel
              projectId={projectId} token={token} type="LOCATION"
              entityList={entityList}
              onUpdateEntity={handleUpdateEntity}
              onNavigateScene={id => viewSceneDetail(id)}
              onRunMigration={handleRunMigration}
            />
          </Card>
        ) : sectionTab === 'characters' ? (
          <Card className="bg-[#141414] border-[#232323] p-6" data-testid="entity-characters-panel">
            <EntityPanel
              projectId={projectId} token={token} type="CHARACTER"
              entityList={entityList}
              onUpdateEntity={handleUpdateEntity}
              onNavigateScene={id => viewSceneDetail(id)}
              onRunMigration={handleRunMigration}
            />
          </Card>
        ) : sectionTab === 'extras' ? (
          /* ── Extras / Background Actors view ── */
          <div data-testid="section-view-extras">
            {(() => {
              const extrasScenes = filteredBreakdown.filter(s => (s.breakdown_items?.EXTRA_BACKGROUND || []).length > 0);
              const totalExtras = filteredBreakdown.reduce((a, s) => a + (s.breakdown_items?.EXTRA_BACKGROUND || []).length, 0);
              return (
                <div>
                  {/* Stats */}
                  <div className="flex items-center gap-6 mb-5 pb-4 border-b" style={{ borderColor: 'var(--cb-border)' }}>
                    <div className="text-center">
                      <div className="text-2xl font-bold" style={{ color: '#a3e635' }}>{totalExtras}</div>
                      <div className="text-xs uppercase tracking-widest mt-0.5" style={{ color: 'var(--cb-text-sec)' }}>Background Actor Groups</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold" style={{ color: 'var(--cb-text)' }}>{extrasScenes.length}</div>
                      <div className="text-xs uppercase tracking-widest mt-0.5" style={{ color: 'var(--cb-text-sec)' }}>Scenes with Extras</div>
                    </div>
                    {/* AI BG Estimation — generate button (superadmin/producer only) */}
                    {canRunExtrasGen && (
                      <button
                        onClick={() => setShowGenExtrasDialog(true)}
                        disabled={extrasBusy}
                        className="ml-auto h-9 px-4 rounded-md text-xs font-semibold uppercase tracking-[0.1em] flex items-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-50"
                        style={{ background: 'var(--cb-accent)', color: '#000' }}
                        data-testid="generate-bg-estimates-btn"
                        title="Run AI to estimate BG actors for every scene"
                      >
                        {extrasBusy ? 'Estimating…' : '✨ Generate BG Estimates'}
                      </button>
                    )}
                  </div>
                  {/* Scene-by-scene extras list */}
                  {extrasScenes.length === 0 ? (
                    <div className="text-center py-12" style={{ color: 'var(--cb-text-sec)' }}>
                      <p>No background actors found.</p>
                      <p className="text-xs mt-2">
                        Use &quot;AI Extract Background Actors&quot; from the Export menu
                        {canRunExtrasGen && (
                          <> or click <span style={{ color: 'var(--cb-accent)' }}>✨ Generate BG Estimates</span> above to estimate crowd sizes per scene.</>
                        )}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {extrasScenes.map(scene => {
                        const extras = scene.breakdown_items?.EXTRA_BACKGROUND || [];
                        const typeStyle = getSceneTypeStyle(scene);
                        const est = scene.extras_estimate || null;
                        const estCount = est ? (est.count ?? '—') : null;
                        const estIsAi = est?.source === 'ai';
                        const isEditing = editingExtrasSceneId === scene.id;
                        return (
                          <div key={scene.id} className="rounded-lg overflow-hidden"
                            style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }}
                            data-testid={`extras-scene-${scene.scene_number ?? 'unnumbered'}`}>
                            {/* Scene header */}
                            <div className="flex items-center gap-3 px-4 py-2.5" style={{ borderBottom: '1px solid var(--cb-border)' }}>
                              <span className="text-sm font-bold tabular-nums" style={{ color: typeStyle.color }}>
                                SC.{formatSceneNumber(scene.scene_number, { padStart: 2 })}
                              </span>
                              <span className="text-xs font-medium" style={{ color: 'var(--cb-text)' }}>{scene.scene_header}</span>
                              <span className="text-xs px-1.5 py-0.5 rounded font-semibold ml-auto" style={{ background: `${typeStyle.color}14`, color: typeStyle.color }}>
                                {typeStyle.label}
                              </span>
                              {/* BG estimate count — inline edit */}
                              <div
                                className="flex items-center gap-1.5 text-xs font-mono"
                                title={est?.reason || (est ? '' : 'No estimate yet')}
                                onClick={e => e.stopPropagation()}
                                data-testid={`extras-estimate-cell-${scene.scene_number ?? 'unnumbered'}`}
                              >
                                <span className="text-[10px] uppercase tracking-widest" style={{ color: 'var(--cb-text-sec)' }}>BG:</span>
                                {isEditing ? (
                                  <input
                                    autoFocus
                                    type="number"
                                    min="0"
                                    value={editExtrasVal}
                                    onChange={e => setEditExtrasVal(e.target.value)}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') handleSaveExtrasEstimate(scene.id, editExtrasVal);
                                      else if (e.key === 'Escape') setEditingExtrasSceneId(null);
                                    }}
                                    onBlur={() => handleSaveExtrasEstimate(scene.id, editExtrasVal)}
                                    className="w-14 px-1.5 py-0.5 rounded font-mono text-xs outline-none"
                                    style={{ background: 'var(--cb-bg)', border: '1px solid var(--cb-accent)', color: 'var(--cb-text)' }}
                                    data-testid={`extras-estimate-input-${scene.scene_number ?? 'unnumbered'}`}
                                  />
                                ) : (
                                  <button
                                    onClick={() => { setEditingExtrasSceneId(scene.id); setEditExtrasVal(String(est?.count ?? '')); }}
                                    className="font-bold px-2 py-0.5 rounded transition-colors hover:opacity-80"
                                    style={{ background: est ? 'rgba(245,166,35,0.10)' : 'transparent', color: est ? 'var(--cb-text)' : 'var(--cb-text-sec)', border: '1px dashed ' + (est ? 'transparent' : 'var(--cb-border)') }}
                                    data-testid={`extras-estimate-edit-btn-${scene.scene_number ?? 'unnumbered'}`}
                                  >
                                    {est ? estCount : '—'}
                                  </button>
                                )}
                                {estIsAi && !isEditing && (
                                  <span
                                    className="text-[9px] font-semibold px-1.5 py-0.5 rounded"
                                    style={{ background: 'rgba(245,166,35,0.18)', color: 'var(--cb-accent)' }}
                                    data-testid={`extras-ai-badge-${scene.scene_number ?? 'unnumbered'}`}
                                    title={est?.reason || ''}
                                  >
                                    ✨ AI
                                  </span>
                                )}
                                {extrasSavedSceneId === scene.id && (
                                  <span className="text-[10px]" style={{ color: '#22c55e' }} data-testid={`extras-saved-tick-${scene.scene_number ?? 'unnumbered'}`}>✓ Saved</span>
                                )}
                              </div>
                              <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(163,230,53,.12)', color: '#a3e635' }}>
                                {extras.length} group{extras.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                            {/* Extras list */}
                            <div className="px-4 py-3 flex flex-wrap gap-2">
                              {extras.map(item => (
                                <span key={item.id} className="text-xs px-2.5 py-1 rounded-full"
                                  style={{ background: 'rgba(163,230,53,.08)', color: '#a3e635', border: '1px solid rgba(163,230,53,.2)' }}>
                                  {getItemText(item.description)}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        ) : sectionTab !== 'scenes' ? (
          /* Other section tabs render the embedded Navigator content */
          <div className="-mx-4 md:-mx-6 -mb-6 lg:-mb-8" data-testid={`section-view-${sectionTab}`}>
            <NavigatorPage embedded initialSection={sectionTab} />
          </div>
        ) : loading ? (
          <div className="text-center py-12 text-[var(--cb-text-sec)]">Loading breakdown...</div>
        ) : filteredBreakdown.length === 0 ? (
          <Card className="bg-[var(--cb-surface)] border-[var(--cb-border)] p-12 text-center">
            <p className="text-[var(--cb-text-sec)]">No scenes found</p>
          </Card>
        ) : viewMode === 'studio' ? (
          /* Studio View — rendered fullscreen outside the inner container */
          <div className="relative -mx-4 md:-mx-6">
            <StudioView
              breakdown={filteredBreakdown}
              projectId={projectId}
              token={token}
              getSynopsis={getSynopsis}
              breakdownLanguage={breakdownLanguage}
              navigate={navigate}
            />
          </div>
        ) : viewMode === 'table' ? (
          /* Excel-like Table View */
          <Card className="bg-[var(--cb-surface)] border-[var(--cb-border)] overflow-x-auto" data-testid="breakdown-table">
            <table className="w-full text-sm">
              <thead style={{ background: DT.surface }} className="sticky top-0 z-10">
                <tr>
                  <th className="text-left p-3 font-semibold border-b" style={{ color: DT.accent, borderColor: DT.border, width: 56 }}>#</th>
                  <th className="text-left p-3 font-semibold border-b min-w-[220px]" style={{ color: DT.textSec, borderColor: DT.border }}>Scene</th>
                  <th className="text-left p-3 font-semibold border-b" style={{ color: DT.textDim, borderColor: DT.border, width: 48 }}>Pg</th>
                  <th className="text-left p-3 font-semibold border-b min-w-[220px]" style={{ color: DT.textDim, borderColor: DT.border }}>Synopsis</th>
                  <th className="text-left p-3 font-semibold border-b" style={{ color: DT.textDim, borderColor: DT.border }}>Cast</th>
                  <th className="text-left p-3 font-semibold border-b" style={{ color: '#a3e635', borderColor: DT.border }}>Bg. Actors</th>
                  {(activeDept === 'ALL' || activeDept === 'PRODUCTION' || activeDept === 'ART') && (
                    <>
                      <th className="text-left p-3 font-semibold border-b" style={{ color: DT.textDim, borderColor: DT.border }}>Props</th>
                      <th className="text-left p-3 font-semibold border-b" style={{ color: DT.textDim, borderColor: DT.border }}>Art/Set</th>
                      <th className="text-left p-3 font-semibold border-b" style={{ color: DT.textDim, borderColor: DT.border }}>P.Vehicles</th>
                    </>
                  )}
                  {(activeDept === 'ALL' || activeDept === 'PRODUCTION' || activeDept === 'COSTUME') && (
                    <th className="text-left p-3 font-semibold border-b" style={{ color: DT.textDim, borderColor: DT.border }}>Costume</th>
                  )}
                  {(activeDept === 'ALL' || activeDept === 'PRODUCTION' || activeDept === 'MAKEUP') && (
                    <th className="text-left p-3 font-semibold border-b" style={{ color: DT.textDim, borderColor: DT.border }}>Makeup</th>
                  )}
                  {(activeDept === 'ALL' || activeDept === 'PRODUCTION' || activeDept === 'OTHERS') && (
                    <>
                      <th className="text-left p-3 font-semibold border-b" style={{ color: '#60A5FA', borderColor: DT.border }}>VFX</th>
                      <th className="text-left p-3 font-semibold border-b" style={{ color: '#F87171', borderColor: DT.border }}>Stunts</th>
                      <th className="text-left p-3 font-semibold border-b" style={{ color: DT.textDim, borderColor: DT.border }}>Sound</th>
                    </>
                  )}
                  <th className="text-left p-3 font-semibold border-b" style={{ color: '#F87171', borderColor: DT.border }}>Flags</th>
                </tr>
              </thead>
              <tbody>
                {filteredBreakdown.map((scene, rowIdx) => {
                  const typeStyle = getSceneTypeStyle(scene);
                  const items = scene.breakdown_items || {};
                  const renderCat = (cat) => {
                    const arr = items[cat] || [];
                    if (arr.length === 0) return <span style={{ color: DT.textDim }}>—</span>;
                    return (
                      <div className="space-y-0.5">
                        {arr.slice(0, 4).map((it) => (
                          <div key={it.id} className="text-xs flex items-center gap-1">
                            <span style={{ color: DT.textSec }}>{it.character_name && <span style={{ color: DT.textPri }}>{it.character_name}: </span>}{getItemText(it.description)}</span>
                            {it.image_paths && it.image_paths.length > 0 && <ImageHoverIndicator paths={it.image_paths} />}
                          </div>
                        ))}
                        {arr.length > 4 && <span className="text-[10px]" style={{ color: DT.textDim }}>+{arr.length - 4} more</span>}
                      </div>
                    );
                  };
                  return (
                    <tr
                      key={scene.id}
                      id={`scene-${scene.scene_number ?? 'unnumbered'}`}
                      className={`cursor-pointer transition-colors ${highlightedSceneId === `scene-${scene.scene_number ?? 'unnumbered'}` ? 'ring-2 ring-yellow-400/60 transition-all duration-500' : ''}`}
                      style={{ borderBottom: `1px solid ${DT.border}`, background: rowIdx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}
                      onMouseEnter={e => e.currentTarget.style.background = DT.cardHover}
                      onMouseLeave={e => e.currentTarget.style.background = rowIdx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)'}
                      onClick={() => viewSceneDetail(scene.id)}
                      data-testid={`table-row-${scene.scene_number ?? 'unnumbered'}`}
                    >
                      <td className="p-3 align-top">
                        <span className="text-sm font-bold tabular-nums" style={{ color: typeStyle.color }}>{formatSceneNumber(scene.scene_number, { padStart: 2 })}</span>
                        {scene.scene_text_status === 'needs_human_review' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); openNHRModal(scene); }}
                            className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold cursor-pointer"
                            style={{ background: 'rgba(251,191,36,0.15)', color: '#FBB724', border: '1px solid rgba(251,191,36,0.3)' }}
                            data-testid={`nhr-badge-${scene.scene_number ?? 'unnumbered'}`}
                          >
                            ⚠ Review scene text
                          </button>
                        )}
                      </td>
                      <td className="p-3 align-top">
                        <div className="font-medium text-xs leading-snug mb-1" style={{ color: DT.textPri }}>{scene.scene_header}</div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold" style={{ background: `${typeStyle.color}14`, color: typeStyle.color }}>{typeStyle.label}</span>
                        </div>
                      </td>
                      <td className="p-3 align-top">
                        <span className="text-xs font-mono" style={{ color: DT.textDim }}>{((scene.page_count_eighths || 0) / 8).toFixed(1)}</span>
                      </td>
                      <td className="p-3 align-top">
                        <div className="flex items-start gap-1.5 flex-wrap">
                          <span className="text-xs" style={{ color: DT.textSec, ...mlSafeStyle(getSynopsis(scene)) }}>{getSynopsis(scene)}</span>
                          <LQSBadge synopsis={scene.synopsis} breakdownLanguage={breakdownLanguage} />
                        </div>
                      </td>
                      <td className="p-3 align-top text-xs" style={{ color: DT.textSec }}>{(scene.characters_in_scene || []).join(', ')}</td>
                      <td className="p-3 align-top">{renderCat('EXTRA_BACKGROUND')}</td>
                      {(activeDept === 'ALL' || activeDept === 'PRODUCTION' || activeDept === 'ART') && (
                        <>
                          <td className="p-3 align-top">{renderCat('PROP')}</td>
                          <td className="p-3 align-top">{renderCat('ART_SET_DRESSING')}</td>
                          <td className="p-3 align-top">{renderCat('PICTURE_VEHICLE')}</td>
                        </>
                      )}
                      {(activeDept === 'ALL' || activeDept === 'PRODUCTION' || activeDept === 'COSTUME') && (
                        <td className="p-3 align-top">{renderCat('COSTUME')}</td>
                      )}
                      {(activeDept === 'ALL' || activeDept === 'PRODUCTION' || activeDept === 'MAKEUP') && (
                        <td className="p-3 align-top">{renderCat('MAKEUP_HAIR')}</td>
                      )}
                      {(activeDept === 'ALL' || activeDept === 'PRODUCTION' || activeDept === 'OTHERS') && (
                        <>
                          <td className="p-3 align-top">{renderCat('VFX')}</td>
                          <td className="p-3 align-top">{renderCat('STUNT')}</td>
                          <td className="p-3 align-top">{renderCat('SOUND_MUSIC')}</td>
                        </>
                      )}
                      <td className="p-3 align-top text-xs" style={{ color: '#F87171' }}>{scene.flag_reason || ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        ) : (
          /* Cards View */
          <div className="space-y-2" data-testid="breakdown-list">
            {filteredBreakdown.map((scene) => {
              const filteredItems = filterItemsByDept(scene.breakdown_items || {});
              const typeStyle = getSceneTypeStyle(scene);
              const TypeIcon = typeStyle.icon;
              const totalItems = Object.values(filteredItems).reduce((s, arr) => s + arr.length, 0);
              const pages = ((scene.page_count_eighths || 0) / 8).toFixed(1);
              return (
              <div
                key={scene.id}
                id={`scene-${scene.scene_number ?? 'unnumbered'}`}
                className={`rounded-xl overflow-hidden cursor-pointer transition-all duration-150 group ${highlightedSceneId === `scene-${scene.scene_number ?? 'unnumbered'}` ? 'ring-2 ring-yellow-400/60' : ''}`}
                style={{
                  background: DT.card,
                  border: `1px solid ${DT.border}`,
                  borderLeft: `3px solid ${typeStyle.color}`,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                }}
                onClick={() => viewSceneDetail(scene.id)}
                onMouseEnter={e => { e.currentTarget.style.background = DT.cardHover; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.4)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = DT.card; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.3)'; e.currentTarget.style.transform = 'none'; }}
                data-testid={`scene-card-${scene.scene_number ?? 'unnumbered'}`}
              >
                <div className="p-4 md:p-5">
                  <div className="flex items-start gap-4 mb-3">
                    {/* Scene number */}
                    <div className="flex-shrink-0 w-12 text-right">
                      <span className="text-2xl font-bold tabular-nums" style={{ color: typeStyle.color, lineHeight: 1 }}>
                        {formatSceneNumber(scene.scene_number, { padStart: 2 })}
                      </span>
                      {scene.scene_text_status === 'needs_human_review' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); openNHRModal(scene); }}
                          className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold cursor-pointer"
                          style={{ background: 'rgba(251,191,36,0.15)', color: '#FBB724', border: '1px solid rgba(251,191,36,0.3)' }}
                          data-testid={`nhr-badge-${scene.scene_number ?? 'unnumbered'}`}
                        >
                          ⚠ Review scene text
                        </button>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      {/* Scene heading */}
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold text-lg leading-snug" style={{ color: DT.textPri, ...mlSafeStyle(scene.scene_header) }}>
                          {scene.scene_header}
                        </h3>
                        {/* flagged badge removed */}
                        <LQSBadge synopsis={scene.synopsis} breakdownLanguage={breakdownLanguage} />
                        <FIPRBadge stage={fiprMap[scene.scene_number]} />
                      </div>
                      {/* Metadata chips */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold" style={{ background: `${typeStyle.color}14`, color: typeStyle.color }}>
                          <TypeIcon className="h-2.5 w-2.5" />{typeStyle.label}
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px]" style={{ background: 'rgba(255,255,255,0.05)', color: DT.textDim }}>
                          <Clock className="h-2.5 w-2.5" />{pages}p
                        </span>
                        {scene.location_name && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px]" style={{ background: 'rgba(255,255,255,0.05)', color: DT.textDim }}>
                            <MapPin className="h-2.5 w-2.5" />{scene.location_name}
                          </span>
                        )}
                        {showEnglishSynopsis && scene.synopsis_en && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded font-medium" style={{ background: 'rgba(96,165,250,0.1)', color: '#60A5FA' }}>EN</span>
                        )}
                      </div>
                    </div>
                    {/* Right: flag toggle + reanalyze + open chevron */}
                    <div className="flex items-center gap-1.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={e => { e.stopPropagation(); handleSceneReanalyze(scene.id); }}
                        disabled={rerunningSceneIds.has(scene.id)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold transition-all disabled:opacity-60"
                        style={{
                          color: DT.textDim,
                          background: 'rgba(255,255,255,0.03)',
                          border: `1px solid ${DT.border}`,
                        }}
                        onMouseEnter={e => {
                          if (rerunningSceneIds.has(scene.id)) return;
                          e.currentTarget.style.color = DT.accent;
                          e.currentTarget.style.borderColor = DT.accent;
                          e.currentTarget.style.background = `${DT.accent}12`;
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.color = DT.textDim;
                          e.currentTarget.style.borderColor = DT.border;
                          e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                        }}
                        title="Reanalyze this scene"
                        data-testid={`reanalyze-scene-btn-${scene.scene_number ?? 'unnumbered'}`}
                      >
                        {rerunningSceneIds.has(scene.id) ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3.5 w-3.5" />
                        )}
                        <span className="hidden sm:inline">Reanalyze</span>
                      </button>
                      <button
                        onClick={e => { e.stopPropagation(); updateSceneField(scene.id, 'is_flagged', !scene.is_flagged); }}
                        className="p-1.5 rounded-lg transition-all"
                        style={{ color: scene.is_flagged ? '#F87171' : DT.textDim, background: scene.is_flagged ? 'rgba(248,113,113,0.1)' : 'transparent' }}
                        title={scene.is_flagged ? 'Remove flag' : 'Flag this scene'}
                        data-testid={`flag-btn-${scene.scene_number ?? 'unnumbered'}`}
                      >
                        <Flag className="h-3.5 w-3.5" />
                      </button>
                      <ChevronRight className="h-4 w-4 opacity-0 group-hover:opacity-60 transition-opacity" style={{ color: DT.textDim }} />
                    </div>
                  </div>

                  {/* Synopsis */}
                  {getSynopsis(scene) && (
                    <p className="text-sm leading-relaxed ml-16 mb-3" style={{ color: DT.textSec, ...mlSafeStyle(getSynopsis(scene)) }}>
                      {getSynopsis(scene)}
                    </p>
                  )}

                  {/* Breakdown chips */}
                  {totalItems > 0 && (
                    <div className="flex flex-wrap gap-1.5 ml-16">
                      {Object.entries(filteredItems).map(([category, items]) =>
                        items.length > 0 ? renderCategoryChip(category, items.length) : null
                      )}
                    </div>
                  )}

                  {/* Remarks */}
                  {(remarksByScene[scene.id] || []).length > 0 && (
                    <div className="mt-3 ml-16 pt-2.5 border-t" style={{ borderColor: 'rgba(232,197,71,0.12)' }}>
                      <span className="text-[9px] font-bold uppercase tracking-widest" style={{ color: DT.accent }}>
                        {remarksByScene[scene.id].length} internal remark{remarksByScene[scene.id].length > 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}

        {/* Scene Detail Dialog — 3-column intelligent workspace */}
        <Dialog open={showSceneDialog} onOpenChange={setShowSceneDialog}>
          <DialogContent className="border-[var(--cb-border)] text-[var(--cb-text)] max-w-6xl max-h-[88vh] overflow-hidden p-0" style={{ background: 'var(--cb-bg)' }}>
            {selectedScene && (() => {
              const st = getSceneTypeStyle(selectedScene);
              const StIcon = st.icon;
              const totalCats = CATEGORIES.filter(c => (selectedScene.breakdown_items?.[c.value] || []).length > 0).length;
              const flagged = selectedScene.is_flagged;
              const extRisk = (selectedScene.location_type || '').toUpperCase() === 'EXT';
              const nightRisk = (selectedScene.time_of_day || '').toUpperCase() === 'NIGHT';
              return (
                <div className="flex flex-col h-full max-h-[88vh]">
                  {/* Header strip */}
                  <div className="flex items-start justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: `1px solid rgba(255,255,255,0.06)`, borderTop: `3px solid ${st.color}` }}>
                    <div className="flex items-center gap-4">
                      <span className="text-3xl font-bold tabular-nums" style={{ color: st.color, fontVariantNumeric: 'tabular-nums' }}>
                        {formatSceneNumber(selectedScene.scene_number, { padStart: 2 })}
                      </span>
                      <div>
                        <DialogTitle className="font-semibold text-xl leading-tight" style={{ color: DT.textPri }}>
                          {selectedScene.scene_header}
                        </DialogTitle>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-md" style={{ background: `${st.color}14`, color: st.color }}>
                            <StIcon className="h-2.5 w-2.5" />{st.label}
                          </span>
                          <span className="text-[10px]" style={{ color: DT.textDim }}>
                            {((selectedScene.page_count_eighths || 0) / 8).toFixed(1)}p
                          </span>
                          {selectedScene.location_name && (
                            <span className="text-[10px] flex items-center gap-1" style={{ color: DT.textDim }}>
                              <MapPin className="h-2.5 w-2.5" />{selectedScene.location_name}
                            </span>
                          )}
                          {totalCats > 0 && (
                            <span className="text-[10px]" style={{ color: DT.textDim }}>{totalCats} depts</span>
                          )}
                          <LQSBadge synopsis={selectedScene.synopsis} breakdownLanguage={breakdownLanguage} />
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                    {/* Phase 13 Ask D — deep-link to Art Dept for Look & Feel */}
                    <button
                      onClick={() => navigate(`/projects/${projectId}/dept/art?scene=${selectedScene.id}`)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                      style={{ background: `${DT.accent}12`, color: DT.accent, border: `1px solid ${DT.accent}30` }}
                      data-testid="mb-look-feel-link"
                    >
                      <Palette className="h-3.5 w-3.5" />
                      Look &amp; Feel
                    </button>
                    <button
                      onClick={() => setDialogTab('script')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${dialogTab === 'script' ? 'text-black' : ''}`}
                      style={dialogTab === 'script'
                        ? { background: '#60A5FA', color: '#000' }
                        : { background: 'rgba(96,165,250,0.1)', color: '#60A5FA', border: '1px solid rgba(96,165,250,0.2)' }}
                      data-testid="view-in-script-btn"
                    >
                      <FileText className="h-3.5 w-3.5" />
                      Script
                    </button>
                    <button
                      onClick={() => setDialogTab('breakdown')}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                      style={dialogTab === 'breakdown'
                        ? { background: DT.accent, color: '#000' }
                        : { background: `${DT.accent}12`, color: DT.accent, border: `1px solid ${DT.accent}30` }}
                      data-testid="view-breakdown-btn"
                    >
                      <Layers className="h-3.5 w-3.5" />
                      Breakdown
                    </button>
                    <button
                      onClick={() => handleSceneReanalyze(selectedScene.id)}
                      disabled={rerunningSceneIds.has(selectedScene.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-60"
                      style={{ background: 'rgba(255,255,255,0.05)', color: DT.textDim, border: `1px solid ${DT.border}` }}
                      onMouseEnter={e => {
                        if (rerunningSceneIds.has(selectedScene.id)) return;
                        e.currentTarget.style.color = DT.accent;
                        e.currentTarget.style.borderColor = DT.accent;
                        e.currentTarget.style.background = `${DT.accent}12`;
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.color = DT.textDim;
                        e.currentTarget.style.borderColor = DT.border;
                        e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                      }}
                      title="Reanalyze this scene"
                      data-testid={`reanalyze-scene-expanded-btn-${selectedScene.scene_number ?? 'unnumbered'}`}
                    >
                      {rerunningSceneIds.has(selectedScene.id) ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3.5 w-3.5" />
                      )}
                      Reanalyze
                    </button>
                    {/* Manual flag toggle */}
                    <button
                      onClick={() => { updateSceneField(selectedScene.id, 'is_flagged', !selectedScene.is_flagged); setSelectedScene(s => s ? { ...s, is_flagged: !s.is_flagged } : s); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                      style={selectedScene.is_flagged
                        ? { background: 'rgba(248,113,113,0.15)', color: '#F87171', border: '1px solid rgba(248,113,113,0.3)' }
                        : { background: 'rgba(255,255,255,0.05)', color: DT.textDim, border: `1px solid ${DT.border}` }}
                      data-testid="dialog-flag-toggle"
                      title={selectedScene.is_flagged ? 'Remove flag' : 'Flag scene'}
                    >
                      <Flag className="h-3.5 w-3.5" />
                      {selectedScene.is_flagged ? 'Flagged' : 'Flag'}
                    </button>
                    <Button onClick={() => setShowEditScene(true)} variant="outline" size="sm" className="border-[var(--cb-border)] text-[var(--cb-text)] hover:bg-[var(--cb-surface)]" data-testid="edit-scene-btn">
                      <Edit2 className="h-3.5 w-3.5 mr-1" />Edit
                    </Button>
                    </div>
                  </div>

                  {/* Script Tab — full-width script view */}
                  {dialogTab === 'script' && (
                    <div className="flex-1 overflow-y-auto p-6" style={{ background: 'var(--cb-bg)' }}>
                      {dialogScriptLoading ? (
                        <div className="flex flex-col items-center justify-center py-20">
                          <div className="h-6 w-6 border-2 border-t-transparent rounded-full animate-spin mb-3" style={{ borderColor: DT.accent }} />
                          {dialogScriptStatus === 'processing' && (
                            <p className="text-xs" style={{ color: DT.textDim }}>Extracting screenplay text…</p>
                          )}
                        </div>
                      ) : dialogScriptText ? (
                        <ScreenplayText text={dialogScriptText} />
                      ) : dialogScriptStatus === 'processing' ? (
                        <div className="flex flex-col items-center justify-center py-20 text-center">
                          <FileText className="h-10 w-10 mb-3" style={{ color: DT.textDim, opacity: 0.3 }} />
                          <p className="text-sm" style={{ color: DT.textSec }}>Script extraction is still processing.</p>
                          <p className="text-xs mt-1" style={{ color: DT.textDim }}>Please try again in a moment.</p>
                        </div>
                      ) : dialogScriptStatus === 'needs_reextract' ? (
                        <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="scene-script-needs-reextract-empty">
                          <RefreshCw className="h-10 w-10 mb-3" style={{ color: DT.accent, opacity: 0.7 }} />
                          <p className="text-sm" style={{ color: DT.textSec }}>Scene text extraction incomplete.</p>
                          <p className="text-xs mt-1 mb-4" style={{ color: DT.textDim }}>This scene was flagged for Vision re-extraction. Re-analyse it now to recover the screenplay text.</p>
                          <Button
                            onClick={() => selectedScene && triggerSceneRerun(selectedScene.id)}
                            disabled={dialogRerunLoading || !selectedScene}
                            size="sm"
                            className="text-xs"
                            style={{ background: DT.accent, color: '#0f0f0f' }}
                            data-testid="scene-script-reanalyze-btn"
                          >
                            {dialogRerunLoading ? (
                              <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Starting…</>
                            ) : (
                              <><RefreshCw className="h-3.5 w-3.5 mr-1.5" />Re-analyze scene</>
                            )}
                          </Button>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-20 text-center">
                          <FileText className="h-10 w-10 mb-3" style={{ color: DT.textDim, opacity: 0.3 }} />
                          <p className="text-sm" style={{ color: DT.textSec }}>No screenplay text available for this scene.</p>
                          <p className="text-xs mt-1" style={{ color: DT.textDim }}>Upload a script PDF to enable scene-level text extraction.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* 3-column body — shown when dialogTab === 'breakdown' */}
                  <div className="flex flex-1 overflow-hidden" style={{ display: dialogTab === 'breakdown' ? 'flex' : 'none' }}>

                    {/* LEFT: Scene context (25%) */}
                    <div className="w-64 flex-shrink-0 overflow-y-auto p-5 space-y-5" style={{ borderRight: `1px solid rgba(255,255,255,0.06)` }}>
                      <div>
                        <div className="text-[9px] font-bold uppercase tracking-widest mb-2" style={{ color: DT.accent, letterSpacing: '0.14em' }}>Synopsis</div>
                        {showEnglishSynopsis && selectedScene.synopsis_en && (
                          <div className="text-[9px] font-medium mb-1" style={{ color: '#60A5FA' }}>EN</div>
                        )}
                        <EditableField
                          value={getSynopsis(selectedScene)}
                          onSave={v => updateSceneField(selectedScene.id, showEnglishSynopsis && selectedScene.synopsis_en ? 'synopsis_en' : 'synopsis', v)}
                          type="textarea"
                          placeholder="Add synopsis…"
                          style={{ color: DT.textSec, fontSize: 13, lineHeight: 1.6 }}
                          data-testid="dialog-synopsis-field"
                        />
                      </div>

                      {/* Location — inline editable */}
                      <div>
                        <div className="text-[9px] font-bold uppercase tracking-widest mb-2" style={{ color: DT.textDim, letterSpacing: '0.1em' }}>Location</div>
                        <div className="flex items-center gap-1.5">
                          <MapPin className="h-3 w-3 flex-shrink-0" style={{ color: '#60A5FA' }} />
                          <EditableField
                            value={selectedScene.location_name}
                            onSave={v => { updateSceneField(selectedScene.id, 'location_name', v); setSelectedScene(s => s ? { ...s, location_name: v } : s); }}
                            placeholder="Add location…"
                            style={{ color: DT.textSec, fontSize: 13 }}
                            data-testid="dialog-location-field"
                          />
                        </div>
                      </div>

                      {/* INT/EXT + Time of Day */}
                      <div className="flex gap-2">
                        <div>
                          <div className="text-[9px] font-bold uppercase tracking-widest mb-1" style={{ color: DT.textDim }}>Type</div>
                          <EditableField
                            value={selectedScene.location_type || ''}
                            onSave={v => { updateSceneField(selectedScene.id, 'location_type', v); setSelectedScene(s => s ? { ...s, location_type: v } : s); }}
                            type="select"
                            options={[{value:'INT',label:'INT'},{value:'EXT',label:'EXT'},{value:'INT/EXT',label:'INT/EXT'}]}
                            style={{ color: DT.textSec, fontSize: 13 }}
                            data-testid="dialog-location-type-field"
                          />
                        </div>
                        <div>
                          <div className="text-[9px] font-bold uppercase tracking-widest mb-1" style={{ color: DT.textDim }}>Time</div>
                          <EditableField
                            value={selectedScene.time_of_day || ''}
                            onSave={v => { updateSceneField(selectedScene.id, 'time_of_day', v); setSelectedScene(s => s ? { ...s, time_of_day: v } : s); }}
                            type="select"
                            options={[{value:'DAY',label:'DAY'},{value:'NIGHT',label:'NIGHT'},{value:'DAWN',label:'DAWN'},{value:'DUSK',label:'DUSK'},{value:'CONTINUOUS',label:'CONTINUOUS'}]}
                            style={{ color: DT.textSec, fontSize: 13 }}
                            data-testid="dialog-time-field"
                          />
                        </div>
                      </div>

                      {/* Characters — add/remove inline with autocomplete */}
                      <div>
                        <div className="text-[9px] font-bold uppercase tracking-widest mb-2" style={{ color: DT.textDim, letterSpacing: '0.1em' }}>Cast</div>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {(selectedScene.characters_in_scene || []).map(c => (
                            <span key={c} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full group/char" style={{ background: 'rgba(255,255,255,0.05)', color: DT.textSec }}>
                              {c}
                              <button
                                className="opacity-0 group-hover/char:opacity-100 transition-opacity"
                                onClick={() => {
                                  const newList = (selectedScene.characters_in_scene || []).filter(x => x !== c);
                                  updateSceneField(selectedScene.id, 'characters_in_scene', newList);
                                  setSelectedScene(s => s ? { ...s, characters_in_scene: newList } : s);
                                }}
                                style={{ color: '#f87171' }}
                                title="Remove character"
                              >×</button>
                            </span>
                          ))}
                        </div>
                        {/* Autocomplete input */}
                        <div className="relative">
                          <input
                            value={charQuery}
                            onChange={e => { setCharQuery(e.target.value); setCharDropdown(true); }}
                            onFocus={() => setCharDropdown(true)}
                            onBlur={() => setTimeout(() => setCharDropdown(false), 150)}
                            placeholder="+ Add character…"
                            className="w-full text-xs rounded px-2 py-1"
                            style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${DT.border}`, color: DT.textPri, outline: 'none' }}
                            onKeyDown={e => {
                              if (e.key === 'Enter' && charQuery.trim()) {
                                const newList = [...(selectedScene.characters_in_scene || []), charQuery.trim().toUpperCase()];
                                updateSceneField(selectedScene.id, 'characters_in_scene', newList);
                                setSelectedScene(s => s ? { ...s, characters_in_scene: newList } : s);
                                setCharQuery('');
                              }
                            }}
                            data-testid="dialog-add-character-input"
                          />
                          {/* Autocomplete dropdown */}
                          {charDropdown && entityList.filter(e => e.type === 'CHARACTER' && e.name.toLowerCase().includes(charQuery.toLowerCase()) && !(selectedScene.characters_in_scene || []).includes(e.name)).length > 0 && (
                            <div className="absolute top-full left-0 right-0 z-50 rounded-lg overflow-hidden mt-1 max-h-40 overflow-y-auto"
                                 style={{ background: DT.surface, border: `1px solid ${DT.border}`, boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
                              {entityList
                                .filter(e => e.type === 'CHARACTER' && e.name.toLowerCase().includes(charQuery.toLowerCase()) && !(selectedScene.characters_in_scene || []).includes(e.name))
                                .sort((a, b) => (b.scene_count || 0) - (a.scene_count || 0))
                                .map(e => (
                                  <button
                                    key={e.id}
                                    onMouseDown={() => {
                                      const newList = [...(selectedScene.characters_in_scene || []), e.name];
                                      updateSceneField(selectedScene.id, 'characters_in_scene', newList);
                                      setSelectedScene(s => s ? { ...s, characters_in_scene: newList } : s);
                                      setCharQuery('');
                                      setCharDropdown(false);
                                    }}
                                    className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-white/5 text-left"
                                    style={{ color: DT.textPri }}
                                    data-testid={`char-suggestion-${e.name}`}
                                  >
                                    <span className="font-medium">{e.name}</span>
                                    <span className="text-[10px]" style={{ color: DT.textDim }}>{e.scene_count || 0} scenes</span>
                                  </button>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Notes — blank for user, supports @mention tagging */}
                      <div>
                        <div className="text-[9px] font-bold uppercase tracking-widest mb-2" style={{ color: DT.textDim }}>Notes</div>
                        <EditableField
                          value={selectedScene.notes || ''}
                          onSave={v => { updateSceneField(selectedScene.id, 'notes', v); setSelectedScene(s => s ? { ...s, notes: v } : s); }}
                          type="textarea"
                          placeholder="Add production notes… Use @name to tag team members."
                          style={{ color: DT.textSec, fontSize: 12, lineHeight: 1.6, minHeight: 72 }}
                          data-testid="dialog-notes-field"
                        />
                        {/* Render @mention highlights */}
                        {selectedScene.notes && /@\w+/.test(selectedScene.notes) && (
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {[...selectedScene.notes.matchAll(/@(\w+)/g)].map((m, i) => (
                              <span key={`tag-${m[1]}-${i}`} className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold"
                                    style={{ background: 'rgba(96,165,250,0.12)', color: '#60A5FA' }}>
                                @{m[1]}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {selectedScene.flag_reason && (
                        <div className="rounded-xl p-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.15)' }}>
                          <div className="text-[9px] font-bold uppercase mb-1" style={{ color: '#F87171' }}>Production Flag</div>
                          <EditableField
                            value={selectedScene.flag_reason}
                            onSave={v => { updateSceneField(selectedScene.id, 'flag_reason', v); setSelectedScene(s => s ? { ...s, flag_reason: v } : s); }}
                            style={{ color: '#F87171', fontSize: 12 }}
                            data-testid="dialog-flag-reason-field"
                          />
                        </div>
                      )}
                    </div>

                    {/* CENTER: Department breakdown (50%) */}
                    <div className="flex-1 overflow-y-auto p-5 space-y-4">
                      {CATEGORIES.map(cat => {
                        const items = selectedScene.breakdown_items?.[cat.value] || [];
                        const isAddingForThisCat = editingItemCategory === cat.value;
                        if (items.length === 0 && !isAddingForThisCat) return null;
                        const chipCfg = CAT_CHIPS[cat.value] || { label: cat.label, color: DT.textSec };
                        return (
                          <div key={cat.value} className="rounded-xl p-4" style={{ background: DT.card, border: `1px solid ${DT.border}` }}>
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: chipCfg.color, letterSpacing: '0.1em' }}>{chipCfg.label} ({items.length})</span>
                              <div className="flex items-center gap-1">
                                {/* Phase 13 Ask B — AI suggest on 3 supported columns only */}
                                {MB_AI_COLUMN_FOR_CATEGORY[cat.value] && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setMbAiPanelColumn(MB_AI_COLUMN_FOR_CATEGORY[cat.value]);
                                      setMbAiPanelAnchor(e.currentTarget.getBoundingClientRect());
                                    }}
                                    className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border"
                                    style={{ borderColor: DT.border, color: DT.accent }}
                                    title="AI suggest for this scene"
                                    data-testid={`mb-ai-suggest-${cat.value}`}>
                                    <Sparkles className="h-2.5 w-2.5" /> AI suggest
                                  </button>
                                )}
                                <Button size="sm" variant="ghost" onClick={() => setEditingItemCategory(cat.value)} className="h-6 px-2 text-[10px]" style={{ color: DT.textDim }} data-testid={`add-item-${cat.value}-btn`}>
                                  <Plus className="h-2.5 w-2.5 mr-1" />Add
                                </Button>
                              </div>
                            </div>
                            <ul className="space-y-1.5">
                              {items.map(item => (
                                <li key={item.id} className="flex items-center gap-2 group/item rounded-lg px-2 py-1.5 transition-all" style={{ background: 'rgba(255,255,255,0.02)' }}
                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}>
                                  <div className="flex-1 min-w-0">
                                    <EditableField
                                      value={getBreakdownItemLabel(item.description, showEnglishSynopsis ? 'english' : 'original')}
                                      onSave={v => updateItemField(item.id, selectedScene.id, 'description', v)}
                                      placeholder="Item name…"
                                      style={{ color: DT.textPri, fontSize: 12, fontWeight: 500 }}
                                      data-testid={`item-desc-${item.id}`}
                                    />
                                    {item.notes && (
                                      <EditableField
                                        value={item.notes}
                                        onSave={v => updateItemField(item.id, selectedScene.id, 'notes', v)}
                                        placeholder="Notes…"
                                        style={{ color: DT.textDim, fontSize: 10, marginTop: 2 }}
                                        data-testid={`item-notes-${item.id}`}
                                      />
                                    )}
                                  </div>
                                  {/* Phase 13 Ask A (2026-07) — always-visible pencil → full 11-field modal */}
                                  <button
                                    onClick={() => setBimEditingItem(item)}
                                    className="p-1 rounded"
                                    style={{ color: DT.textDim }}
                                    onMouseEnter={e => e.currentTarget.style.color = DT.accent}
                                    onMouseLeave={e => e.currentTarget.style.color = DT.textDim}
                                    title="Edit all fields"
                                    data-testid={`mb-item-edit-${item.id}`}
                                  >
                                    <Pencil className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={async () => {
                                      await axios.delete(`${API}/projects/${projectId}/scenes/${selectedScene.id}/breakdown/${item.id}`, { headers: { Authorization: `Bearer ${token}` } });
                                      viewSceneDetail(selectedScene.id);
                                    }}
                                    className="opacity-0 group-hover/item:opacity-100 transition-opacity p-1 rounded"
                                    style={{ color: '#f87171' }}
                                    title="Remove"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </li>
                              ))}
                              {isAddingForThisCat && (
                                <li>
                                  <BreakdownItemEditor isNew item={{ category: cat.value }} projectId={projectId} sceneId={selectedScene.id} onUpdate={() => viewSceneDetail(selectedScene.id)} onCancel={() => setEditingItemCategory(null)} />
                                </li>
                              )}
                            </ul>
                          </div>
                        );
                      })}
                      {/* Show all empty categories for quick add */}
                      <div className="grid grid-cols-3 gap-2">
                        {CATEGORIES.filter(cat => !(selectedScene.breakdown_items?.[cat.value] || []).length && editingItemCategory !== cat.value).map(cat => {
                          const chipCfg = CAT_CHIPS[cat.value] || { label: cat.label, color: DT.textDim };
                          return (
                            <div key={cat.value} className="relative">
                              <button onClick={() => setEditingItemCategory(cat.value)} className="w-full rounded-lg p-2.5 text-left transition-all" style={{ background: 'rgba(255,255,255,0.03)', border: `1px dashed ${DT.border}` }} onMouseEnter={e => e.currentTarget.style.background = DT.card} onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'} data-testid={`add-item-${cat.value}-btn`}>
                                <div className="text-[9px] font-semibold" style={{ color: DT.textDim }}>{chipCfg.label}</div>
                                <div className="text-[9px] mt-0.5" style={{ color: DT.textDim, opacity: 0.5 }}>+ Add</div>
                              </button>
                              {/* Phase 13 Defect 2 fix — AI suggest also on empty rows */}
                              {MB_AI_COLUMN_FOR_CATEGORY[cat.value] && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMbAiPanelColumn(MB_AI_COLUMN_FOR_CATEGORY[cat.value]);
                                    setMbAiPanelAnchor(e.currentTarget.getBoundingClientRect());
                                  }}
                                  className="absolute top-1 right-1 flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded border"
                                  style={{ borderColor: DT.border, color: DT.accent, background: DT.card }}
                                  title="AI suggest for this scene"
                                  data-testid={`mb-ai-suggest-${cat.value}`}>
                                  <Sparkles className="h-2 w-2" /> AI
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* RIGHT: AI + Comments (25%) */}
                    <div className="w-64 flex-shrink-0 overflow-y-auto p-5 space-y-4" style={{ borderLeft: `1px solid rgba(255,255,255,0.06)` }}>
                      {/* Shot List Generator */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-1.5">
                            <Film className="h-3 w-3" style={{ color: '#60A5FA' }} />
                            <span className="text-[9px] font-bold uppercase tracking-widest" style={{ color: '#60A5FA', letterSpacing: '0.12em' }}>Shot List</span>
                          </div>
                          <button
                            onClick={() => generateShotList(selectedScene.id)}
                            disabled={loadingShots}
                            className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg transition-all disabled:opacity-50"
                            style={{ background: 'rgba(96,165,250,0.1)', color: '#60A5FA', border: '1px solid rgba(96,165,250,0.2)' }}
                            data-testid="generate-shot-list-btn"
                          >
                            {loadingShots ? <><span className="animate-spin">⟳</span> Generating…</> : <><Sparkles className="h-2.5 w-2.5" />Suggest</>}
                          </button>
                        </div>
                        {shotList === null && !loadingShots && (
                          <p className="text-[10px]" style={{ color: DT.textDim }}>Click &ldquo;Suggest&rdquo; for AI camera shot suggestions.</p>
                        )}
                        {shotList && shotList.length > 0 && (
                          <div className="space-y-1.5 max-h-64 overflow-y-auto">
                            {shotList.map((shot, i) => (
                              <div key={`shot-${shot.shot || i}-${shot.type || ''}`} className="rounded-lg p-2" style={{ background: 'rgba(96,165,250,0.05)', border: '1px solid rgba(96,165,250,0.12)' }}>
                                <div className="flex items-center gap-2 mb-0.5">
                                  <span className="text-[9px] font-bold font-mono" style={{ color: '#60A5FA' }}>{shot.shot}</span>
                                  <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded" style={{ background: 'rgba(96,165,250,0.15)', color: '#60A5FA' }}>{shot.type}</span>
                                  <span className="text-[9px]" style={{ color: DT.textDim }}>{shot.movement}</span>
                                  {shot.lens && <span className="text-[9px]" style={{ color: DT.textDim }}>{shot.lens}</span>}
                                </div>
                                <p className="text-[10px] leading-snug" style={{ color: DT.textSec }}>{shot.description}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        {shotList && shotList.length === 0 && (
                          <p className="text-[10px]" style={{ color: DT.textDim }}>No shots generated. Try adding a synopsis first.</p>
                        )}
                      </div>

                      {/* Scene Intelligence */}
                      <div>
                        <div className="flex items-center gap-1.5 mb-3">
                          <Sparkles className="h-3 w-3" style={{ color: DT.accent }} />
                          <span className="text-[9px] font-bold uppercase tracking-widest" style={{ color: DT.accent, letterSpacing: '0.12em' }}>Scene Intelligence</span>
                        </div>
                        <div className="space-y-2">
                          {flagged && <div className="rounded-lg p-2.5" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.15)' }}><p className="text-[10px]" style={{ color: '#F87171' }}>⚠ Flagged — requires special prep</p></div>}
                          {extRisk && <div className="rounded-lg p-2.5" style={{ background: 'rgba(251,146,60,0.08)', border: '1px solid rgba(251,146,60,0.15)' }}><p className="text-[10px]" style={{ color: '#FB923C' }}>☁ EXT scene — weather dependent</p></div>}
                          {nightRisk && <div className="rounded-lg p-2.5" style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.15)' }}><p className="text-[10px]" style={{ color: '#8B5CF6' }}>🌙 Night shoot — extended setup time</p></div>}
                          {(selectedScene.characters_in_scene || []).length > 4 && (
                            <div className="rounded-lg p-2.5" style={{ background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.15)' }}><p className="text-[10px]" style={{ color: '#60A5FA' }}>🎭 {selectedScene.characters_in_scene.length} cast — coordinate scheduling early</p></div>
                          )}
                          {((selectedScene.page_count_eighths || 0) / 8) > 5 && (
                            <div className="rounded-lg p-2.5" style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.15)' }}><p className="text-[10px]" style={{ color: '#34D399' }}>⏱ Heavy scene ({((selectedScene.page_count_eighths || 0) / 8).toFixed(1)}p) — plan for full day</p></div>
                          )}
                          {!flagged && !extRisk && !nightRisk && (selectedScene.characters_in_scene || []).length <= 4 && (
                            <p className="text-[10px]" style={{ color: DT.textDim }}>No production risks detected.</p>
                          )}
                        </div>
                      </div>

                      {/* Comments */}
                      <div className="border-t pt-4" style={{ borderColor: DT.border }}>
                        <div className="text-[9px] font-bold uppercase tracking-widest mb-3 flex items-center gap-1" style={{ color: DT.textDim, letterSpacing: '0.1em' }}>
                          <MessageSquare className="h-3 w-3" />
                          Notes ({comments.length})
                        </div>
                        <div className="mb-3">
                          <CommentInput value={newComment} onChange={setNewComment} users={projectUsers} onSubmit={handlePostComment} disabled={postingComment} />
                        </div>
                        <div className="space-y-2" data-testid="comments-list">
                          {comments.map(comment => (
                            <div key={comment.id} className="rounded-xl p-3" style={{ background: DT.card, border: `1px solid ${DT.border}` }} data-testid={`comment-${comment.id}`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-medium" style={{ color: DT.textPri }}>{comment.user_name}</span>
                                {(comment.user_id === user.id || user.role === 'ADMIN' || user.role === 'PRODUCER') && (
                                  <Button variant="ghost" size="sm" onClick={() => handleDeleteComment(comment.id)} className="h-5 w-5 p-0 text-red-400" data-testid={`delete-comment-${comment.id}`}>
                                    <Trash2 className="h-2.5 w-2.5" />
                                  </Button>
                                )}
                              </div>
                              <p className="text-xs" style={{ color: DT.textSec }}>{renderCommentWithMentions(comment.content, projectUsers)}</p>
                              <p className="text-[10px] mt-1" style={{ color: DT.textDim }}>{new Date(comment.created_at).toLocaleString()}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Phase 13 Ask A — full-field edit modal (reused from Art Dept) */}
        <BreakdownItemEditModal
          open={!!bimEditingItem}
          item={bimEditingItem}
          projectId={projectId}
          token={token}
          onClose={() => setBimEditingItem(null)}
          onSaved={() => { setBimEditingItem(null); if (selectedScene) viewSceneDetail(selectedScene.id); }}
        />
        {/* Phase 13 Ask B — AI suggest panel (reused from Art Dept) */}
        <AiSuggestPanel
          open={!!mbAiPanelColumn}
          anchorRect={mbAiPanelAnchor}
          projectId={projectId}
          token={token}
          sceneId={selectedScene?.id}
          column={mbAiPanelColumn || 'scene_prop'}
          existingNames={(() => {
            if (!mbAiPanelColumn || !selectedScene) return [];
            const backMap = { action_prop: 'PROP', scene_prop: 'SCENE_PROP', set_dressing: 'ART_SET_DRESSING' };
            const cat = backMap[mbAiPanelColumn];
            // Defect 3 fix — breakdown_items is a dict keyed by category, not
            // an array. Read the target bucket directly and default to [].
            const bi = selectedScene.breakdown_items;
            const bucket = Array.isArray(bi) ? bi.filter(i => i.category === cat) : (bi?.[cat] || []);
            return bucket.map(i => i.description).filter(Boolean);
          })()}
          onClose={() => { setMbAiPanelColumn(null); setMbAiPanelAnchor(null); }}
          onChipAdded={() => { if (selectedScene) viewSceneDetail(selectedScene.id); }}
        />

        {/* AI Import Modal */}
      {showAIImport && (
        <AIImportModal
          projectId={projectId}
          token={token}
          onClose={() => setShowAIImport(false)}
          onImportComplete={() => { fetchBreakdown(); setShowAIImport(false); }}
        />
      )}

      {/* Script view modal — opened via "View Script" button in scene detail */}
      {showScriptForScene && (
        <SceneViewModal
          scene={showScriptForScene}
          projectId={projectId}
          token={token}
          defaultTab="script"
          onClose={() => setShowScriptForScene(null)}
        />
      )}

      {/* Edit Scene Dialog */}
        <SceneEditDialog 
          open={showEditScene}
          onClose={() => setShowEditScene(false)}
          scene={selectedScene}
          projectId={projectId}
          onSaved={() => {
            fetchBreakdown();
            viewSceneDetail(selectedScene.id);
          }}
        />

        {/* ── Floating Fifi AI Button ──────────────────────────────────── */}
        <button
          data-testid="ai-correction-fab"
          onClick={openAiChat}
          className="fixed bottom-20 right-6 z-40 flex items-center gap-2 px-4 py-3 rounded-full bg-[#F5A623] text-black font-semibold text-sm shadow-lg hover:bg-[#d6b539] transition-all duration-200 hover:scale-105"
        >
          <Wand2 className="h-4 w-4" />
          Fifi AI
        </button>

        {/* ── AI Chat Slide-in Panel ────────────────────────────────────── */}
        {showAiChat && (
          <div className="fixed inset-0 z-50 flex justify-end" onClick={(e) => { if (e.target === e.currentTarget) setShowAiChat(false); }}>
            <div className="w-full max-w-md bg-[#0d0d0d] border-l border-[var(--cb-border)] flex flex-col h-full shadow-2xl">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--cb-border)] flex-shrink-0">
                <div className="flex items-center gap-2">
                  <Wand2 className="h-4 w-4 text-[#F5A623]" />
                  <span className="font-semibold text-lg text-[var(--cb-text)]">Fifi AI</span>
                  <span className="text-[10px] text-[#606060] bg-[var(--cb-surface)] border border-[var(--cb-border)] rounded px-1.5 py-0.5 ml-1">Breakdown Assistant</span>
                </div>
                <button
                  onClick={() => setShowAiChat(false)}
                  className="text-[#606060] hover:text-[var(--cb-text)] transition-colors"
                  data-testid="close-ai-chat-btn"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
                {chatMessages.map((msg) => (
                  <div key={msg._msgId || msg.role + msg.content.slice(0, 20)} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${
                      msg.role === 'user'
                        ? 'bg-[#F5A623] text-black rounded-br-sm'
                        : 'bg-[var(--cb-surface)] border border-[var(--cb-border)] text-[#e0e0e0] rounded-bl-sm'
                    }`}>
                      {msg.content.split('\n').map((line, li) => renderMessageLine(line, li))}
                      {msg.applied && msg.applied.length > 0 && (
                        <div className="mt-3 pt-2 border-t border-[#ffffff15] space-y-1">
                          {msg.applied.map((line, ai) => (
                            // applied lines are stable within a message — content is unique enough as key
                            <div key={`${msg._msgId}-applied-${ai}`} className="flex items-start gap-1.5 text-xs opacity-80">
                              <CheckCircle2 className="h-3 w-3 mt-0.5 flex-shrink-0 text-[#44bb88]" />
                              <span>{line}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {chatLoading && (
                  <div className="flex justify-start">
                    <div className="bg-[var(--cb-surface)] border border-[var(--cb-border)] rounded-2xl rounded-bl-sm px-4 py-3">
                      <div className="flex gap-1 items-center">
                        <span className="h-2 w-2 bg-[#F5A623] rounded-full animate-bounce" style={{animationDelay:'0ms'}} />
                        <span className="h-2 w-2 bg-[#F5A623] rounded-full animate-bounce" style={{animationDelay:'150ms'}} />
                        <span className="h-2 w-2 bg-[#F5A623] rounded-full animate-bounce" style={{animationDelay:'300ms'}} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Action buttons — only shown for non-English breakdown */}
              <div className="px-4 pt-3 pb-1 flex-shrink-0 border-t border-[#1e1e1e] space-y-2">
                <button
                  data-testid="translate-synopses-btn"
                  onClick={translateAllSynopses}
                  disabled={translating || chatLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[var(--cb-surface)] border border-[var(--cb-border)] hover:border-[#F5A623]/50 hover:bg-[var(--cb-surface-h)] transition-all duration-150 disabled:opacity-50 group"
                >
                  <Globe className="h-4 w-4 text-[#F5A623] group-hover:scale-110 transition-transform" />
                  <span className="text-sm text-[#c0c0c0] group-hover:text-[var(--cb-text)]">
                    {translating ? 'Translating synopses…' : 'Translate all synopses to English'}
                  </span>
                  {translating && (
                    <span className="ml-1 flex gap-0.5">
                      {[0, 150, 300].map(d => (
                        <span key={d} className="h-1.5 w-1.5 bg-[#F5A623] rounded-full animate-bounce" style={{animationDelay:`${d}ms`}} />
                      ))}
                    </span>
                  )}
                </button>
                {/* Generate EN synopses — stores in synopsis_en, keeps original intact */}
                {breakdownLanguage !== 'english' && (
                  <button
                    data-testid="gen-en-synopses-btn"
                    onClick={async () => {
                      try {
                        const { data } = await axios.post(
                          `${API}/projects/${projectId}/generate-english-synopses`,
                          {},
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        toast.success(data.message);
                        if (data.translated > 0) fetchBreakdown();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to generate English synopses');
                      }
                    }}
                    disabled={chatLoading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[var(--cb-surface)] border border-[var(--cb-border)] hover:border-[#4a9eff]/50 hover:bg-[var(--cb-surface-h)] transition-all duration-150 disabled:opacity-50 group"
                  >
                    <Languages className="h-4 w-4 text-[#4a9eff] group-hover:scale-110 transition-transform" />
                    <span className="text-sm text-[#c0c0c0] group-hover:text-[var(--cb-text)]">Generate English synopses (EN toggle)</span>
                  </button>
                )}
                {/* Fix garbled legacy-font encoding */}
                {breakdownLanguage !== 'english' && (
                  <button
                    data-testid="fix-encoding-btn"
                    onClick={async () => {
                      toast.info('Detecting and fixing garbled text...');
                      try {
                        const { data } = await axios.post(
                          `${API}/projects/${projectId}/fix-synopsis-encoding`,
                          {},
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        if (data.fixed > 0) {
                          toast.success(`Fixed ${data.fixed} garbled synopsis(es)`);
                          fetchBreakdown();
                        } else {
                          toast.success(data.message || 'No garbled text found');
                        }
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Fix encoding failed');
                      }
                    }}
                    disabled={chatLoading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[var(--cb-surface)] border border-[var(--cb-border)] hover:border-emerald-500/50 hover:bg-[var(--cb-surface-h)] transition-all duration-150 disabled:opacity-50 group"
                  >
                    <Sparkles className="h-4 w-4 text-emerald-400 group-hover:scale-110 transition-transform" />
                    <span className="text-sm text-[#c0c0c0] group-hover:text-[var(--cb-text)]">Fix Legacy Font Encoding</span>
                  </button>
                )}
                {/* Fix corrupted scene headers (from bad Excel imports) */}
                {breakdownLanguage !== 'english' && (
                  <button
                    data-testid="fix-headers-btn"
                    onClick={async () => {
                      toast.info('Checking scene headers...');
                      try {
                        const { data } = await axios.post(
                          `${API}/projects/${projectId}/fix-scene-headers`,
                          {},
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        if (data.fixed > 0) {
                          toast.success(`Reconstructed ${data.fixed} scene header(s)`);
                          fetchBreakdown();
                        } else {
                          toast.success('All scene headers look correct');
                        }
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Fix headers failed');
                      }
                    }}
                    disabled={chatLoading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[var(--cb-surface)] border border-[var(--cb-border)] hover:border-[#F5A623]/50 hover:bg-[var(--cb-surface-h)] transition-all duration-150 disabled:opacity-50 group"
                  >
                    <Film className="h-4 w-4 text-[#F5A623] group-hover:scale-110 transition-transform" />
                    <span className="text-sm text-[#c0c0c0] group-hover:text-[var(--cb-text)]">Fix Scene Headers</span>
                  </button>
                )}
              </div>

              {/* Examples (shown only before any user message) */}
              {chatMessages.filter(m => m.role === 'user').length === 0 && (
                <div className="px-4 pb-2 flex flex-wrap gap-1.5 flex-shrink-0">
                  {correctionExamples.map((ex) => (
                    <button
                      key={ex}
                      onClick={() => sendChatMessage(ex)}
                      className="text-xs px-2 py-1 rounded-md bg-[var(--cb-surface)] border border-[var(--cb-border)] text-[#707070] hover:text-[var(--cb-text)] hover:border-[#F5A623]/40 transition-colors text-left"
                    >
                      {ex}
                    </button>
                  ))}
                </div>
              )}

              {/* Input */}
              <div className="px-4 pb-4 pt-2 border-t border-[var(--cb-border)] flex-shrink-0">
                <div className="flex gap-2 items-end">
                  <textarea
                    ref={chatInputRef}
                    data-testid="chat-input"
                    value={chatInput}
                    onChange={e => setChatInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendChatMessage();
                      }
                    }}
                    placeholder="Type a correction… (Enter to send)"
                    rows={2}
                    className="flex-1 bg-[var(--cb-surface)] border border-[var(--cb-border)] rounded-xl px-3 py-2 text-sm text-[var(--cb-text)] placeholder:text-[#505050] resize-none focus:outline-none focus:border-[#F5A623]/60"
                    disabled={chatLoading}
                  />
                  <button
                    data-testid="send-chat-btn"
                    onClick={() => sendChatMessage()}
                    disabled={!chatInput.trim() || chatLoading}
                    className="flex-shrink-0 w-9 h-9 rounded-xl bg-[#F5A623] disabled:opacity-40 text-black flex items-center justify-center hover:bg-[#d6b539] transition-colors"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-xs text-[#404040] mt-1.5">Shift+Enter for new line • Corrections apply instantly</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* ── Export Language Dialog ── */}
    {showExportLangDialog && pendingExport && (
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
           style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
           onClick={e => e.target === e.currentTarget && setShowExportLangDialog(false)}>
        <div className="rounded-2xl border border-[#2a2a2a] p-6 w-full max-w-sm shadow-2xl"
             style={{ background: '#141414' }}>
          <h3 className="text-base font-bold text-white mb-1">Export: {pendingExport.label}</h3>
          <p className="text-xs text-[#707070] mb-5">Choose language for this PDF export.</p>

          <div className="mb-5">
            <p className="text-xs font-semibold text-[#a0a0a0] mb-2 uppercase tracking-wider">Synopsis Language</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { val: 'english',  label: 'English',         desc: 'synopsis_en field' },
                { val: 'original', label: 'Original Language', desc: 'synopsis field (Malayalam/Tamil etc.)' },
              ].map(opt => (
                <button key={opt.val}
                  onClick={() => setSynopsisLangExp(opt.val)}
                  className="text-left rounded-lg border p-2.5 transition-all text-xs"
                  style={{
                    borderColor: synopsisLangExp === opt.val ? '#e8c547' : '#2a2a2a',
                    background:  synopsisLangExp === opt.val ? 'rgba(232,197,71,0.08)' : '#1a1a1a',
                    color:       synopsisLangExp === opt.val ? '#e8c547' : '#a0a0a0',
                  }}>
                  <div className="font-semibold mb-0.5">{opt.label}</div>
                  <div className="text-[9px] opacity-60">{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="text-[10px] text-[#505050] mb-5 px-1">
            Cast names are always fetched from <code>characters_in_scene</code> — exactly as shown in Breakdown. No transliteration.
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setShowExportLangDialog(false)}
              className="flex-1 h-9 rounded-lg text-xs font-medium border border-[#2a2a2a] text-[#a0a0a0] hover:text-white transition-colors">
              Cancel
            </button>
            <button
              onClick={confirmExport}
              className="flex-1 h-9 rounded-lg text-xs font-bold"
              style={{ background: '#e8c547', color: '#000' }}>
              Download PDF
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ── Generate BG Extras Confirmation Dialog ── */}
    {showGenExtrasDialog && (
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
           style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
           onClick={e => e.target === e.currentTarget && setShowGenExtrasDialog(false)}
           data-testid="generate-bg-estimates-dialog">
        <div className="rounded-2xl border border-[#2a2a2a] p-6 w-full max-w-md shadow-2xl"
             style={{ background: '#141414' }}>
          <h3 className="text-base font-bold text-white mb-1">✨ Generate BG Actor Estimates</h3>
          <p className="text-xs text-[#a0a0a0] mb-4 leading-relaxed">
            This will run an AI pass on every scene without a manual override and write an estimated crowd count
            (with a one-line reason). Scenes you have already edited manually will be skipped.
          </p>
          <p className="text-[11px] text-[#707070] mb-5">
            Uses your daily AI generation limit. If the cap is hit mid-run, remaining scenes are skipped — no silent burn.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setShowGenExtrasDialog(false)}
              className="flex-1 h-9 rounded-lg text-xs font-medium border border-[#2a2a2a] text-[#a0a0a0] hover:text-white transition-colors"
              data-testid="generate-bg-estimates-cancel-btn">
              Cancel
            </button>
            <button
              onClick={handleGenerateExtras}
              className="flex-1 h-9 rounded-lg text-xs font-bold"
              style={{ background: '#e8c547', color: '#000' }}
              data-testid="generate-bg-estimates-confirm-btn">
              Generate
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ── NHR Side-by-Side Review Modal ─────────────────────────────── */}
    {nhrScene && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" data-testid="nhr-modal">
        <div className="relative w-[95vw] max-w-[1400px] h-[90vh] rounded-xl overflow-hidden flex flex-col"
             style={{ background: '#0f0f0f', border: '1px solid rgba(251,191,36,0.3)' }}>
          <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#222' }}>
            <h2 className="text-lg font-semibold" style={{ color: '#FBB724' }}>
              Review Scene Text — Scene {formatSceneNumber(nhrScene.scene_number)}
              {nhrPageNumber != null && <span className="text-sm text-gray-400 ml-2">(page {nhrPageNumber})</span>}
            </h2>
            <button onClick={closeNHRModal}
                    className="px-3 py-1 rounded text-sm hover:bg-white/5"
                    style={{ color: '#aaa' }}
                    data-testid="nhr-close-btn">✕</button>
          </div>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-0 overflow-hidden">
            <div className="border-r flex flex-col" style={{ borderColor: '#222' }}>
              <div className="px-4 py-2 text-xs uppercase tracking-wider" style={{ color: '#888', background: '#111' }}>
                Original PDF Page
              </div>
              {nhrPageNumber != null ? (
                <iframe
                  src={`${API}/projects/${projectId}/script/pdf#page=${nhrPageNumber ?? 1}`}
                  title="Original PDF Page"
                  className="flex-1 w-full bg-white"
                  data-testid="nhr-pdf-iframe"
                />
              ) : (
                <div className="flex-1 flex items-center justify-center text-sm" style={{ color: '#666' }}>
                  No page available for this scene
                </div>
              )}
            </div>
            <div className="flex flex-col">
              <div className="px-4 py-2 text-xs uppercase tracking-wider" style={{ color: '#888', background: '#111' }}>
                Extracted Text Preview (≤ 300 chars)
              </div>
              <pre className="flex-1 p-4 overflow-auto text-sm whitespace-pre-wrap font-mono"
                   style={{ color: '#ddd', background: '#0a0a0a' }}
                   data-testid="nhr-preview-text">
                {(nhrScene.scene_text_vision_rejected_text_preview || '').slice(0, 300) || '(no preview available)'}
              </pre>
              <div className="flex items-center justify-end gap-3 px-4 py-3 border-t" style={{ borderColor: '#222' }}>
                <button
                  onClick={() => handleNHRDecision('reject')}
                  disabled={nhrLoading}
                  className="px-4 py-2 rounded text-sm font-semibold disabled:opacity-50"
                  style={{ background: 'rgba(220,38,38,0.15)', color: '#ef4444', border: '1px solid rgba(220,38,38,0.3)' }}
                  data-testid="nhr-reject-btn">
                  Reject (keep manual)
                </button>
                <button
                  onClick={() => handleNHRDecision('approve')}
                  disabled={nhrLoading || !nhrScene.scene_text_vision_rejected_text_preview}
                  className="px-4 py-2 rounded text-sm font-semibold disabled:opacity-50"
                  style={{ background: '#FBB724', color: '#000' }}
                  data-testid="nhr-approve-btn">
                  Approve extracted text
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Global Reanalyze confirmation dialog */}
    <AlertDialog open={showGlobalReanalyzeDialog} onOpenChange={setShowGlobalReanalyzeDialog}>
      <AlertDialogContent
        className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)]"
        data-testid="reanalyze-script-dialog"
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Reanalyze entire script?</AlertDialogTitle>
          <AlertDialogDescription className="text-[var(--cb-text-dim)]">
            This will reprocess the entire script with AI and may take a few minutes.
            Existing scenes and breakdown items will be replaced.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={globalReanalyzeInFlight}
            className="bg-transparent border-[var(--cb-border)] text-[var(--cb-text)]"
            data-testid="reanalyze-script-cancel"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleGlobalReanalyze}
            disabled={globalReanalyzeInFlight}
            className="bg-[#F5A623] hover:bg-[#d6b539] text-black font-medium"
            data-testid="reanalyze-script-confirm"
          >
            {globalReanalyzeInFlight ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Starting…</>
            ) : (
              'Reanalyze'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};
export default MasterBreakdownPage;
